import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ApplicationType, ResourcePropertyDto, SyncPhaseDto } from 'src/app/services/dtos'
import { Arn } from 'src/utils/types'
import { ResourceType, ResourceTypeDetails, ResourceVariant } from 'src/utils/resource-types'
import { ResourcesTab } from 'src/app/account/components/application/resources-tab'
import { ProcessingStatus } from '../process-state/process-state.component'
import { RaisedIssue } from '../../services/issue'
import { Table } from 'primeng/table'
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators'
import { SelectItem } from 'primeng/api'
import { BehaviorSubject, Subscription } from 'rxjs'
import { UiService } from 'src/app/services/ui.service'
import { LoggerService } from '../../services/logger.service'
import { ResourceConfig, S3BucketConfig } from '../../services/sync-pair.service'
import { Application } from '../../services/application.service'
import { handleArpioError } from 'src/utils/handle-error'
import { CONFIRMING_STATUS_MESSAGE, FINALIZING_STATUS_MESSAGE } from 'src/app/shared/resource-viewer-base'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'

export enum ResourceStatus {
  // these values are the display-precedence used to arbitrate between showing staging
  // and sync status for a given resource when we're showing combined status.
  NO_STATUS = 0,
  SUCCEEDED = 1,
  PENDING = 2,
  PROCESSING = 3,
  FAILED = 4,
  QUARANTINED = 5,
  SCAN_CLEAN = 6,
  SCAN_INFECTED = 7,
  SCAN_FAILED = 8,
  UNSATISFIED_DEPENDENCY = 9,
  NOT_PROCESSED = 10, // Resource was not processed because an upstream dependency failed
}

// Determines status label wording and NO_STATUS/NOT_PROCESSED merge behavior
export enum StatusContext {
  BACKUP = 'backup',
  RECOVERY = 'recovery',
}

// Generic child group for expandable parent rows (e.g., K8s resources under EKS clusters).
// Keyed by the parent's resourceArn in the expandableGroups Map.
interface ExpandableChildGroup {
  parentArn: string
  children: ViewableApplicationResource[] // sorted children (lazy-initialized on first expand)
  filteredChildren: ViewableApplicationResource[] // after applying active filters; rendered directly (no cap)
  rawChildren: ViewableApplicationResource[] // unsorted, stored for lazy init
  initialized: boolean // whether children have been sorted (lazy init flag)
  expanded: boolean
  loading: boolean // deferred sort/filter on first expand
  autoExpanded: boolean // true if expanded by search match (collapses on search clear)
  // True when any descendant is in a NEEDS_ATTENTION_STATUSES_* trigger state for the active context.
  // Drives the "Needs attention" pill that replaces the parent's own status pill on the row.
  // Passive indicator only — the issue icon next to it is the click target for filtering.
  needsAttention: boolean
  // Per-trigger-status counts (e.g. "3 Not Processed, 1 Failed"). Drives the pill tooltip so a user
  // can see exactly which descendants flipped the rollup without scrolling thousands of children.
  needsAttentionTooltip: string
  // PROCESSING > PENDING > null. When non-null and the parent's own status is "settled" (not in
  // a needs-attention trigger and not already PROCESSING), it overrides the parent's pill so a
  // cluster doesn't read "Success" while pods are still restoring/backing up.
  rollupInProgressStatus: ResourceStatus | null
  issueCount: number // children with issues that pass current global filters; drives the parent's issue-icon visibility
  childCount: number
  issuesOnly: boolean // per-parent issues-only filter
  // Per-group escape hatch: when true, _rebuildFilteredChildren skips ALL global filter axes for
  // this group so the user can see children even when global pills (type/status/text) excluded them.
  // Cleared on collapse, on clearAllFilters(), or by toggling again.
  bypassGlobalFilters: boolean
  applyingIssues: boolean // transient: issue-icon mid-click; drives the issue-icon spinner
  parentRow?: ViewableApplicationResource // back-reference so filter passes can stamp _childIssueCount in O(1)
}

const QUARANTINE_STATUS_TYPES = new Set([
  ResourceStatus.QUARANTINED,
  ResourceStatus.SCAN_CLEAN,
  ResourceStatus.SCAN_INFECTED,
  ResourceStatus.SCAN_FAILED,
])

// Statuses that count as a "problem" on a child resource — drives the parent row's
// "Needs attention" rollup pill and the click-through filter that narrows children to those states.
// NO_STATUS is intentionally excluded from RECOVERY: it shares the "Not Processed" label with
// NOT_PROCESSED in the UI, but a true skip-due-to-upstream-failure (NOT_PROCESSED) is the only
// recovery state that warrants attention. NO_STATUS just means "no embedded status reported yet".
const NEEDS_ATTENTION_STATUSES_BACKUP = new Set<ResourceStatus>([
  ResourceStatus.FAILED,
  ResourceStatus.SCAN_INFECTED,
  ResourceStatus.SCAN_FAILED,
  ResourceStatus.NOT_PROCESSED,
])

const NEEDS_ATTENTION_STATUSES_RECOVERY = new Set<ResourceStatus>([
  ResourceStatus.FAILED,
  ResourceStatus.UNSATISFIED_DEPENDENCY,
  ResourceStatus.SCAN_INFECTED,
  ResourceStatus.SCAN_FAILED,
  ResourceStatus.NOT_PROCESSED,
])

const S3_OBJECT_REPLICATION_MODES = new Map([
  ['none', 'Bucket only'],
  ['realTime', 'Bucket and contents'],
  ['snapshot', 'Bucket and contents (Snapshot)'],
  [undefined, 'Bucket only (not configured)'],
])

export function isStatusQuarantineType(status: ResourceStatus): boolean {
  return QUARANTINE_STATUS_TYPES.has(status)
}

export interface ViewableApplicationResource {
  resourceType: ResourceType
  resourceVariant: ResourceVariant
  resourceArn: Arn
  targetResourceArn?: Arn
  sourceResourceName: string
  sourceResourceGroup?: string
  targetResourceName: string
  targetResourceGroup?: string
  resourceProperties: Array<ResourcePropertyDto>
  status: ResourceStatus
  progress: number
  message: string
  issues: Set<RaisedIssue>
  displayResourceType: string
  substatus?: string // Explanatory text for status
  embeddedStatusTimestamp?: Date // Timestamp from the scope-specific embedded status
  lastStatusTimestamp?: Date // Timestamp of the most recent status change
  hasIssues?: boolean // Whether this resource has associated issues
  sourceResourcePortalUri?: string
  targetResourcePortalUri?: string
  resourcePortalName?: string
  resourceTypeDetails: ResourceTypeDetails
  parentArn?: string // ARN of parent resource for child grouping (e.g., K8s resource → EKS cluster)
}

export interface MultiSelectCategory {
  name: number | string
  label: string
}

export interface FilterPillConfig {
  id: string | number
  label: string
}

export interface FilterSectionConfig {
  key: string
  label: string
  pills: FilterPillConfig[]
}

// Status display config: label (context-aware) + CSS class in one place.
// CSS class is shared across backup/recovery — only labels differ.
interface StatusDisplayConfig {
  label: string
  cssClass: string
}

const STATUS_CSS_CLASSES: Record<ResourceStatus, string> = {
  [ResourceStatus.NO_STATUS]: 'not-processed',
  [ResourceStatus.SUCCEEDED]: 'succeeded',
  [ResourceStatus.PENDING]: 'pending',
  [ResourceStatus.PROCESSING]: 'processing',
  [ResourceStatus.FAILED]: 'failed',
  [ResourceStatus.QUARANTINED]: 'quarantined',
  [ResourceStatus.SCAN_CLEAN]: 'clean',
  [ResourceStatus.SCAN_INFECTED]: 'infected',
  [ResourceStatus.SCAN_FAILED]: 'scan-failed',
  [ResourceStatus.UNSATISFIED_DEPENDENCY]: 'blocked',
  [ResourceStatus.NOT_PROCESSED]: 'not-processed',
}

const BACKUP_STATUS_LABELS: Record<ResourceStatus, string> = {
  [ResourceStatus.NO_STATUS]: 'Not Started',
  [ResourceStatus.SUCCEEDED]: 'Ready',
  [ResourceStatus.PENDING]: 'Queued',
  [ResourceStatus.PROCESSING]: 'Backing Up',
  [ResourceStatus.FAILED]: 'Failed',
  [ResourceStatus.QUARANTINED]: 'Quarantined',
  [ResourceStatus.SCAN_CLEAN]: 'Clean',
  [ResourceStatus.SCAN_INFECTED]: 'Infected',
  [ResourceStatus.SCAN_FAILED]: 'Scan Failed',
  [ResourceStatus.UNSATISFIED_DEPENDENCY]: 'Blocked',
  [ResourceStatus.NOT_PROCESSED]: 'Not Processed',
}

// NO_STATUS and NOT_PROCESSED both display as "Not Processed" because from the user's
// perspective neither was restored. They differ internally: NO_STATUS = never processed or
// status expired; NOT_PROCESSED = skipped due to upstream dependency failure. The status filter
// merges both into a single "Not Processed" count (see getStatusCount).
const RECOVERY_STATUS_LABELS: Record<ResourceStatus, string> = {
  [ResourceStatus.NO_STATUS]: 'Not Processed',
  [ResourceStatus.SUCCEEDED]: 'Success',
  [ResourceStatus.PENDING]: 'Queued',
  [ResourceStatus.PROCESSING]: 'Restoring',
  [ResourceStatus.FAILED]: 'Failed',
  [ResourceStatus.QUARANTINED]: 'Quarantined',
  [ResourceStatus.SCAN_CLEAN]: 'Clean',
  [ResourceStatus.SCAN_INFECTED]: 'Infected',
  [ResourceStatus.SCAN_FAILED]: 'Scan Failed',
  [ResourceStatus.UNSATISFIED_DEPENDENCY]: 'Blocked',
  [ResourceStatus.NOT_PROCESSED]: 'Not Processed',
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'resource-status-pill',
  templateUrl: './resource-status-pill.component.html',
  styleUrls: ['./resource-status-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResourceStatusPillComponent implements OnInit {
  @Input() status: ResourceStatus
  @Input() progress: 0
  @Input() context: StatusContext = StatusContext.RECOVERY
  @Input() substatus: string = ''
  // When true, renders a pointer cursor/hover state and emits pillClick. Used to turn
  // NOT_PROCESSED pills into links that jump the table to the upstream FAILED resources.
  @Input() clickable: boolean = false

  @Output() pillClick = new EventEmitter<MouseEvent>()

  // Expose ResourceStatus enum to template
  ResourceStatus = ResourceStatus

  get compositeProgress() {
    if (this.status === ResourceStatus.PROCESSING) {
      return this.progress
    }
    return 1
  }

  get compositeProgressPercent() {
    return Math.round(this.compositeProgress * 100)
  }

  get statusLabel(): string {
    // PENDING normally renders as "Queued" (resource queued for an active job).
    // The processRecentlyCompleted bridge in resource-viewer-base sets the substatus to
    // CONFIRMING_STATUS_MESSAGE — discriminate on it and render "Pending" so the label
    // matches the post-job-lag semantic instead of the queue semantic.
    if (this.status === ResourceStatus.PENDING && this.substatus === CONFIRMING_STATUS_MESSAGE) {
      return 'Pending'
    }
    // Interrupted mid-restore (job ended before this resource finalized) — see resource-viewer-base.
    if (this.status === ResourceStatus.PENDING && this.substatus === FINALIZING_STATUS_MESSAGE) {
      return 'Finalizing'
    }
    const labels = this.context === StatusContext.BACKUP ? BACKUP_STATUS_LABELS : RECOVERY_STATUS_LABELS
    return labels[this.status] || 'Unknown'
  }

  get statusClass(): string {
    return STATUS_CSS_CLASSES[this.status] || 'not-processed'
  }

  onPillClick(event: MouseEvent) {
    if (!this.clickable) return
    event.stopPropagation()
    this.pillClick.emit(event)
  }

  ngOnInit() {}
}

@Component({
  selector: 'app-resources-view',
  templateUrl: './app-resources-view.component.html',
  styleUrls: ['./app-resources-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [CopyFeedbackService],
})
export class AppResourcesViewComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  expandedRows = new Set<string>()
  // Tracks rows where truncated cell text is expanded (separate from detail row expansion).
  // Uses Set for O(1) lookups — isCellExpanded() is called per-row per-column on every
  // change detection cycle. Virtual scroll only activates above 3000 resources, so below
  // that threshold all rows render at once (e.g., 2000 rows × 2 cols = 4000 calls/cycle).
  // Set.has() is nanosecond-scale, so even 4000 lookups is well under 1ms — not a concern.
  cellExpandedRows = new Set<string>()
  skeletonRows = Array(20)

  @Input() resources: ViewableApplicationResource[]
  @Input() tabType = ResourcesTab.RECOVERY_ENVIRONMENT
  @Input() appStatus: ProcessingStatus = null
  @Input() appIsDeleted: boolean = false
  // Default to true so skeleton rows show until the parent explicitly signals loading is done.
  // Prevents empty-state flash when the component is recreated during tab switches.
  @Input() loading: boolean = true
  @Input() application: Application = null
  @Input() initialFilterText: string = '' // Filter text passed from parent to persist across component recreation
  @Input() statusContext: StatusContext = StatusContext.RECOVERY // Context for status labels

  @Output() filterTextChange = new EventEmitter<string>() // Emit filter changes to parent for persistence
  @Output() issuesClicked = new EventEmitter<ViewableApplicationResource>() // Emit when user clicks issues indicator

  // Once true, prevents empty-state flash during PrimeNG re-renders
  _hasEverHadResources: boolean = false

  // Display rows: regular resources + expandable parent rows (children stored separately).
  // Children are rendered outside PrimeNG's [value] so global filter doesn't hide them.
  displayRows: any[] = []
  // Generic expandable parent-child groups, keyed by parent resourceArn.
  // Each parent row (e.g., EKS cluster) can have children (e.g., K8s resources) that
  // render below it when expanded. Replaces the K8s-specific grouping.
  expandableGroups: Map<string, ExpandableChildGroup> = new Map()

  // Cached values to avoid repeated iterations in template
  private _shouldShowResourceGroupColumn: boolean = false
  private _globalFilterFields: string[] = []
  private _notEmptyList: boolean = false
  private _cachedStatusesLabels: MultiSelectCategory[] = []
  private _cachedUniqueResourceGroups: string[] = []
  private _cachedStatusSummary = {
    total: 0,
    succeeded: 0,
    failed: 0,
    processing: 0,
    pending: 0,
    blocked: 0,
    notProcessed: 0,
  }
  // Pre-computed pill counts to avoid O(pills * resources) per CD cycle
  private _cachedStatusCounts: Map<ResourceStatus, number> = new Map()
  private _cachedResourceTypeCounts: Map<string, number> = new Map()
  private _cachedResourceGroupCounts: Map<string, number> = new Map()
  private _cachedIssuesCount: number = 0
  // Count of resources with raw status NOT_PROCESSED (upstream-dependency-failed), excluding
  // NO_STATUS pilot-light rows. Used to surface the upstream-failure notice.
  private _cachedUpstreamBlockedCount: number = 0
  // Pre-computed per-resource display values to avoid method calls in templates.
  // Keyed by resourceArn. Rebuilt in ngOnChanges when resources or component state changes.
  _cachedIssueTooltips: Map<string, string | null> = new Map()
  _cachedRecoveryPlaceholders: Map<string, string> = new Map()
  _cachedPrimaryIsPlaceholder: Map<string, boolean> = new Map()
  _cachedPrimaryPlaceholderText: Map<string, string> = new Map()

  private lastEmittedFilterText: string = ''
  filterText: string = ''
  lastFilterEvent: Event | null = null // Added to store the last filter event

  @ViewChild('dt1') resourcesTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  resourceCategory: MultiSelectCategory[] = []
  selectedCategories: string[]
  selectedStatuses: number[]
  statusCategories: MultiSelectCategory[]
  isFilterApplied: boolean = false
  representatives!: any[]
  totalRecords = 0
  // Holds the loading skeleton on the table until the initial parent-persisted filter
  // has been applied, so PrimeNG never paints a frame of unfiltered rows.
  _initialFilterPending = false
  private resourceConfigSubscriptions: Subscription[] = []
  private debounceSubscription: Subscription
  debounceSubject: BehaviorSubject<string> = new BehaviorSubject<string>('') // A subject to handle debounce logic for search input delay
  loadingFilterInput: boolean = false // Loading flag to control the spinner visibility
  loadingFilterBy: boolean = false // True while pill-driven filter pipeline runs; swaps caret for spinner on the Filter by button
  resourceConfigs: Map<string, ResourceConfig> = new Map<string, ResourceConfig>() // Arn -> resourceConfig

  // Status labels are now computed based on context.
  // In recovery context, NO_STATUS and NOT_PROCESSED are merged into a single "Not processed" entry.
  get statusesLabels(): MultiSelectCategory[] {
    return this._cachedStatusesLabels
  }

  private buildStatusesLabels(): MultiSelectCategory[] {
    const labels = this.statusContext === StatusContext.BACKUP ? BACKUP_STATUS_LABELS : RECOVERY_STATUS_LABELS
    const result: MultiSelectCategory[] = [
      { label: labels[ResourceStatus.SUCCEEDED], name: ResourceStatus.SUCCEEDED },
      { label: labels[ResourceStatus.PENDING], name: ResourceStatus.PENDING },
      { label: labels[ResourceStatus.PROCESSING], name: ResourceStatus.PROCESSING },
      { label: labels[ResourceStatus.FAILED], name: ResourceStatus.FAILED },
      { label: labels[ResourceStatus.QUARANTINED], name: ResourceStatus.QUARANTINED },
      { label: labels[ResourceStatus.SCAN_CLEAN], name: ResourceStatus.SCAN_CLEAN },
      { label: labels[ResourceStatus.SCAN_INFECTED], name: ResourceStatus.SCAN_INFECTED },
      { label: labels[ResourceStatus.SCAN_FAILED], name: ResourceStatus.SCAN_FAILED },
      { label: labels[ResourceStatus.UNSATISFIED_DEPENDENCY], name: ResourceStatus.UNSATISFIED_DEPENDENCY },
      { label: labels[ResourceStatus.NOT_PROCESSED], name: ResourceStatus.NOT_PROCESSED },
    ]
    // In backup context, NO_STATUS is a distinct "Not started" status
    if (this.statusContext === StatusContext.BACKUP) {
      result.push({ label: labels[ResourceStatus.NO_STATUS], name: ResourceStatus.NO_STATUS })
    }
    return result
  }

  // Unified filter sections for the dropdown template
  _filterSections: FilterSectionConfig[] = []

  trackBySectionKey(_index: number, section: FilterSectionConfig): string {
    return section.key
  }

  trackByPillId(_index: number, pill: FilterPillConfig): string | number {
    return pill.id
  }

  /** Rebuild cached filter sections when resources change. */
  updateFilterSections(): void {
    const statusPills: FilterPillConfig[] = this.statusesLabels
      .filter((s) => this.getStatusCount(s.name as ResourceStatus) > 0)
      .map((s) => ({ id: s.name, label: s.label }))
    if (this.getIssuesCount() > 0) {
      statusPills.push({ id: 'issues', label: 'Issues' })
    }
    const sections: FilterSectionConfig[] = [
      { key: 'status', label: 'Status', pills: statusPills },
      {
        key: 'resourceType',
        label: 'Resource Type',
        pills: this.resourceCategory.map((c) => ({ id: c.name, label: c.name as string })),
      },
    ]

    if (this._shouldShowResourceGroupColumn) {
      sections.push({
        key: 'resourceGroup',
        label: 'Resource Group',
        pills: this.uniqueResourceGroups.map((g) => ({ id: g, label: g })),
      })
    }

    this._filterSections = sections
  }

  isPillActive(sectionKey: string, pillId: string | number): boolean {
    switch (sectionKey) {
      case 'status':
        if (pillId === 'issues') return this.issuesFilterActive
        return this.selectedStatusFilters.includes(pillId as ResourceStatus)
      case 'resourceType':
        return this.selectedResourceTypes.includes(pillId as string)
      case 'resourceGroup':
        return this.selectedResourceGroups.includes(pillId as string)
      default:
        return false
    }
  }

  getPillCount(sectionKey: string, pillId: string | number): number {
    switch (sectionKey) {
      case 'status':
        if (pillId === 'issues') return this.getIssuesCount()
        return this.getStatusCount(pillId as ResourceStatus)
      case 'resourceType':
        return this.getResourceTypeCount(pillId as string)
      case 'resourceGroup':
        return this.getResourceGroupCount(pillId as string)
      default:
        return 0
    }
  }

  // Letter strip for resource type quick-filter
  activeLetterFilter: string | null = null
  _resourceTypeLetters: string[] = []

  /** Compute unique first letters from resource categories, sorted A-Z. */
  updateResourceTypeLetters(): void {
    const letters = new Set<string>()
    this.resourceCategory.forEach((c) => {
      const label = c.name as string
      if (label) letters.add(label.charAt(0).toUpperCase())
    })
    this._resourceTypeLetters = Array.from(letters).sort()
  }

  toggleLetterFilter(letter: string): void {
    this.activeLetterFilter = this.activeLetterFilter === letter ? null : letter
  }

  /** Whether a pill should be visible given the active letter filter. */
  isPillVisibleForLetter(sectionKey: string, pill: FilterPillConfig): boolean {
    if (sectionKey !== 'resourceType' || !this.activeLetterFilter) return true
    return (pill.label as string).charAt(0).toUpperCase() === this.activeLetterFilter
  }

  onFilterPillClick(sectionKey: string, pillId: string | number): void {
    switch (sectionKey) {
      case 'status':
        if (pillId === 'issues') this.toggleIssuesFilter()
        else this.toggleStatusFilter(pillId as ResourceStatus)
        break
      case 'resourceType':
        this.toggleResourceTypeFilter(pillId as string)
        break
      case 'resourceGroup':
        this.toggleResourceGroupFilter(pillId as string)
        break
    }
  }

  private resizeObserver: ResizeObserver | null = null

  constructor(
    private uiService: UiService,
    private logger: LoggerService,
    private el: ElementRef,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    public copyFeedback: CopyFeedbackService
  ) {}

  /**
   * Manages the filtering of resources based on the provided filterText.
   * Updates the input field and applies the filter to the resources table.
   * If the filterText is empty, clears the input and resets the filter.
   *
   * @param {string} filterText - ARP-2910
   *  The text used for filtering the resources.
   *  If empty, clears the input field and resets the filter.
   *  If non-empty, applies the filter to the resources table.
   */
  transferkeywordToAllApplications(filterText: string) {
    this.filterInput.nativeElement.value = filterText
    this.filterText = filterText
    this.debounceSubject.next(filterText)
  }

  ngOnInit() {
    this.getResourceTypeValues()
    this._cachedStatusesLabels = this.buildStatusesLabels()
    this._initialFilterPending = !!this.initialFilterText
    this.logger.info('Resources view loaded', { component: 'ResourcesPage' })
  }

  ngOnDestroy(): void {
    this.clear(this.resourcesTable)
    this.debounceSubscription?.unsubscribe()
    this.resizeObserver?.disconnect()
    this.el.nativeElement.removeEventListener('mouseover', this.onTruncatableHover)
    this.resourceConfigSubscriptions.forEach((s) => s.unsubscribe())
    this.resourceConfigSubscriptions = []
  }

  // Lazy first-hover truncation stamp. mouseover bubbles, so a single delegated listener
  // catches every cell. The data-truncation-checked guard ensures scrollWidth (a forced-reflow
  // read) runs at most ONCE per cell, total — never on subsequent hovers. Stale flags after a
  // resize are acceptable; resource names are stable. Runs outside Angular zone — no CD.
  private onTruncatableHover = (event: Event): void => {
    const t = event.target as HTMLElement
    if (!t.classList?.contains('cell-truncatable')) return
    if (t.dataset.truncationChecked) return
    t.dataset.truncationChecked = '1'
    if (t.scrollWidth > t.clientWidth) t.classList.add('is-truncated')
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resources) {
      this.resources = changes.resources.currentValue
      // Flip on first definitive resolution (any non-null array, including []) so a genuinely
      // empty scope routes to the empty state instead of skeletons. Never reset to false —
      // transient empty arrays during polls would cause empty-state flash; the component is
      // destroyed and recreated by *ngIf on tab switch, which naturally resets the flag.
      if (this.resources != null) {
        this._hasEverHadResources = true
      }
    }

    // Recompute cached values when relevant inputs change.
    // Resource Group column visibility is app-type-based only (Azure) — intentionally
    // NOT gated on resources.some() so the header stays visible for Azure apps during
    // empty/loading states instead of popping in once data arrives.
    if (changes.resources || changes.application || changes.appIsDeleted) {
      this._shouldShowResourceGroupColumn = this.application?.type === ApplicationType.AZURE
      this.updateGlobalFilterFields()
    }

    if (changes.resources || changes.tabType || changes.appStatus) {
      this.updateNotEmptyList()
    }

    // Re-measure scroll height when loading state or resources change (footer may appear/resize)
    if (changes.loading || changes.resources) {
      setTimeout(() => {
        this.adjustScrollHeight()
        this.cdr.markForCheck()
      })
    }

    // Rebuild cached derived values when relevant inputs change.
    // Pill counts MUST be rebuilt before filter sections so updateFilterSections()
    // sees current counts when filtering out zero-count statuses.
    if (changes.statusContext) {
      this._cachedStatusesLabels = this.buildStatusesLabels()
    }
    if (changes.resources || changes.statusContext) {
      this._cachedUniqueResourceGroups = this.buildUniqueResourceGroups()
      this._cachedStatusSummary = this.buildStatusSummary()
      this.rebuildPillCounts()
      this.rebuildPerResourceCaches()
    }

    if (changes.resources) {
      this.getResourceTypeValues()

      // When the table view child isn't wired yet (resources present before ngAfterViewInit —
      // e.g. switching back to a tab whose scope is still cached), there can't be a user-applied
      // sort, so default sorting is always safe. Gating on resourcesTable alone dropped the
      // default sort on tab switch-back, leaving rows in raw backend order.
      if (!this.resourcesTable || !this.resourcesTable.sortField) {
        // No custom sort has been applied.  Apply default sort ordering by type then name.
        this.resources = this.resources.sort((a, b) => {
          const typeCompare = (a.displayResourceType || '').localeCompare(b.displayResourceType || '')
          if (typeCompare !== 0) return typeCompare
          return (a.sourceResourceName || '').localeCompare(b.sourceResourceName || '')
        })
      }
    }

    // Rebuild display rows after all resource/filter state is settled
    if (changes.resources) {
      this.buildDisplayRows()
    }

    // Reapply filter AFTER buildDisplayRows so the filter runs against the new rows,
    // not the stale ones. Fixes the bug where filtered text shows "no results" after
    // a polling update replaces the resource array. Gated on changes.resources because
    // applyFilter emits filterTextChange, which the parent echoes back via initialFilterText —
    // an unguarded retrigger would re-run the full filter pipeline twice per keystroke.
    if (changes.resources && this.resourcesTable) {
      if (this.lastFilterEvent) {
        this.applyFilter((this.lastFilterEvent.target as HTMLInputElement).value, this.resourcesTable)
      } else if (this._initialFilterPending && this.initialFilterText) {
        // Resources arrived after view init — apply the initial filter now.
        this.applyInitialFilter()
      }
    }
  }
  ngAfterViewInit() {
    if (this.resourcesTable != null) {
      this.resourcesTable.resetScrollTop = function () {}
    }
    // Defer initial measurement so the DOM has settled after tab switch
    setTimeout(() => {
      this.adjustScrollHeight()
      this.cdr.markForCheck()
    })

    // ResizeObserver runs outside Angular zone to avoid triggering CD on every resize event.
    // Only the scroll height recalculation callback re-enters the zone.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        // Skip re-entering the zone when the measured offset is unchanged —
        // observer fires frequently during reflow, and markForCheck() isn't free.
        if (!this.adjustScrollHeight()) return
        this.zone.run(() => this.cdr.markForCheck())
      })
      // Observe the nearest scrollable ancestor (content-block) for size changes
      const parent = this.el.nativeElement.closest('.content-block') || this.el.nativeElement.parentElement
      if (parent) {
        this.resizeObserver.observe(parent)
      }

      // Lazy truncation stamp — delegated, outside zone, first-hover-only per cell.
      this.el.nativeElement.addEventListener('mouseover', this.onTruncatableHover)
    })

    // skip(1) drops the BehaviorSubject's initial '' replay-on-subscribe — otherwise
    // it would flow through the debounce, fire applyFilter(''), and clobber the
    // parent's persisted filterText on the next app navigation.
    this.debounceSubscription = this.debounceSubject
      .pipe(skip(1), debounceTime(300), distinctUntilChanged())
      .subscribe((filterText: string) => {
        // loadingFilterInput stays true through the heavy pipeline and is cleared at the
        // end of applyFilter, so the input spinner spans both the debounce and the actual
        // filter work — important on accounts where filtering thousands of k8s children
        // takes seconds.
        this.applyFilter(filterText, this.resourcesTable)
        this.cdr.markForCheck()
      })

    // Apply parent's persisted filter synchronously while the table is still in its
    // loading state, so the first paint is filtered rows (or skeleton) — never the
    // unfiltered list. If resources haven't arrived yet, the pending flag stays true
    // and ngOnChanges applies the filter once they do.
    if (this.initialFilterText && this.resourcesTable && this.resources?.length) {
      this.applyInitialFilter()
    } else if (!this.initialFilterText) {
      this._initialFilterPending = false
    }
  }

  private applyInitialFilter(): void {
    // lastEmittedFilterText is preset so applyFilter()'s emit-back guard suppresses
    // the echo to the parent (which would otherwise round-trip back as a new input).
    this.lastEmittedFilterText = this.initialFilterText.toLowerCase().trim()
    this.filterInput.nativeElement.value = this.initialFilterText
    this.applyFilter(this.initialFilterText, this.resourcesTable)
    this._initialFilterPending = false
    this.cdr.markForCheck()
  }

  /**
   * Gets resources categories based on the resources array.
   */
  getResourceCategory() {
    if (!this.resources) return
    // Use a Map to store unique categories with string keys for uniqueness
    const uniqueCategoriesMap = new Map<string, MultiSelectCategory>()

    this.resources.forEach((resource) => {
      // Create a unique key (resourceType) for each resource type based on 'label' and 'name'
      const key = resource.resourceType + ':' + resource.displayResourceType

      // Check if the key is already added to the map to ensure uniqueness
      if (!uniqueCategoriesMap.has(key)) {
        // If not present, add it to the Map
        uniqueCategoriesMap.set(key, {
          label: resource.resourceType,
          name: resource.displayResourceType,
        })
      }
    })

    // Convert the map values to an array for the resourceCategory
    this.resourceCategory = Array.from(uniqueCategoriesMap.values())
  }

  /**
   * Gets available status categories based on the resources and statusesLabels array.
   * Statuses with name value 0 will appear at the end of the result.
   */
  getAvailableStatuses(): void {
    if (!this.resources) return
    // Use a Map to store unique status categories with a combination of label and name as the key
    const uniqueStatusMap = new Map<string, MultiSelectCategory>()

    // Iterate through resources and add unique status categories
    this.resources.forEach((resource) => {
      // Find the corresponding label for the status from statusesLabels
      const statusLabel = this.statusesLabels.find((status) => status.name === resource.status)
      if (statusLabel) {
        // Form a unique key using label and name to ensure uniqueness
        const key = statusLabel.label + ':' + resource.status

        // Add to the Map if not already present
        if (!uniqueStatusMap.has(key)) {
          uniqueStatusMap.set(key, {
            label: statusLabel.label,
            name: resource.status,
          })
        }
      }
    })

    // Convert the map values to an array
    const uniqueStatusArray = Array.from(uniqueStatusMap.values())

    // Sort the array so that items with name value 0 come at the end
    uniqueStatusArray.sort((a, b) => {
      const nameA = a.name
      const nameB = b.name
      if (nameA === 0 && nameB !== 0) {
        return 1 // 0 comes after other values
      } else if (nameA !== 0 && nameB === 0) {
        return -1 // Other values come before 0
      } else {
        return 0 // Maintain order for other values
      }
    })

    // Assign the sorted statuses to the statusCategories
    this.statusCategories = uniqueStatusArray
  }

  getResourceTypeValues() {
    this.getResourceCategory()
    this.getAvailableStatuses()
    this.updateFilterSections()
    this.updateResourceTypeLetters()
  }

  filterResourcesByCategory(selectedCategories: any[], filterDT?: any, event?: any): void {
    this.selectedCategories = selectedCategories
    filterDT(selectedCategories)
  }

  removeResCategoryTag(categoryToRemove: string, filter?: any, value?: any[]): void {
    value = value.filter((category) => category !== categoryToRemove)
    this.selectedCategories = value
    this.filterResourcesByCategory(value, filter)
  }

  filterStatusFunc(selectedStatuses: any[], filter?: any): void {
    this.selectedStatuses = selectedStatuses
    filter(selectedStatuses)
  }

  removeStatusTag(statusToRemove: SelectItem, value: any, filter: any): void {
    value = value.filter((category) => category !== statusToRemove)
    this.selectedStatuses = value
    // Re-filter resources based on updated selectedCategories
    this.filterStatusFunc(value, filter)
  }

  // Dynamic scroll height: measured from table container top to viewport bottom, minus footer/padding
  scrollHeight: string = 'calc(100vh - 347px)'
  private _lastScrollHeightOffset: number = 347

  // Returns true when scrollHeight changed so callers (e.g. ResizeObserver) can
  // skip redundant change-detection when layout is stable.
  adjustScrollHeight(): boolean {
    const hostEl = this.el.nativeElement
    const scrollContainer = hostEl.querySelector('.p-datatable-table-container')
    if (!scrollContainer) return false
    const scrollTop = scrollContainer.getBoundingClientRect().top

    // Sum the height of all sibling elements after the scroll container (footer, tfoot, etc.)
    let belowHeight = 0
    let sibling = scrollContainer.nextElementSibling
    while (sibling) {
      belowHeight += sibling.getBoundingClientRect().height
      sibling = sibling.nextElementSibling
    }

    // Account for bottom padding of the .content-block parent container
    const contentBlock = hostEl.closest('.content-block')
    const parentPaddingBottom = contentBlock ? parseFloat(getComputedStyle(contentBlock).paddingBottom) || 0 : 0

    const offset = Math.round(scrollTop + belowHeight + parentPaddingBottom)
    // Only update if the offset actually changed — prevents feedback loops
    if (offset === this._lastScrollHeightOffset) return false
    this._lastScrollHeightOffset = offset
    // On mobile, stacked header content can push offset > viewport height,
    // collapsing the table. Use max() to enforce a minimum usable height.
    this.scrollHeight = `max(300px, calc(100vh - ${offset}px))`
    return true
  }

  filterGlobal(event: any): void {
    // Save the most recent event so we can retrigger filtering on ngOnChanges
    this.lastFilterEvent = event
    this.loadingFilterInput = true
    const filterText = event.target.value

    // Paste is one intent — coalescing it through the 300ms keystroke debounce just delays
    // results. Detect via InputEvent.inputType and run applyFilter immediately, but defer
    // one frame via rAF so the spinner paints before the (potentially seconds-long) filter
    // pipeline blocks the UI thread. Typing still benefits from debounce coalescing.
    if (event?.inputType === 'insertFromPaste') {
      this.cdr.detectChanges()
      requestAnimationFrame(() => {
        this.applyFilter(filterText, this.resourcesTable)
        this.cdr.markForCheck()
      })
      return
    }
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string, dt: Table): void {
    if (!dt || typeof filterText !== 'string') {
      this.loadingFilterInput = false
      return
    }
    // Preserve sort state before clearing filters
    const currentSortField = dt.sortField
    const currentSortOrder = dt.sortOrder

    // Clear text-related filters before applying new ones.
    // Preserve resourceArn filter if dropdown pill filters are active —
    // otherwise typing in the search bar would silently clear dropdown selections.
    delete dt.filters['global']
    delete dt.filters['status']
    if (!this.hasActiveColumnFilters) {
      delete dt.filters['resourceArn']
    }

    const filterValue = filterText.toLowerCase().trim()

    if (!filterValue) {
      // Apply the cleared filter state for the empty-search path
      dt._filter()
      this.filterText = filterValue
      this.isFilterApplied = false
      // Collapse auto-expanded parent groups when search is cleared
      this.syncAndAutoExpand('')
      // Emit change to parent for persistence
      if (filterValue !== this.lastEmittedFilterText) {
        this.lastEmittedFilterText = filterValue
        this.filterTextChange.emit(filterValue)
      }
      // Still restore sort state even when clearing filter
      if (currentSortField) {
        dt.sortField = currentSortField
        dt.sortOrder = currentSortOrder
        // Force PrimeNG to re-evaluate sort state
        setTimeout(() => {
          if (dt.sortField === currentSortField) {
            dt.sortSingle()
          }
        }, 0)
      }
      this.loadingFilterInput = false
      return
    }

    // Single pass over expandable groups: stamps _childSearchMatch on parents (so PrimeNG's
    // global filter keeps them visible when children match) AND toggles auto-expand state.
    this.syncAndAutoExpand(filterValue)

    // Use global text filter for all searches. Status label matching happens
    // naturally via _statusLabel (stamped per-row in rebuildPerResourceCaches), so typing
    // "ready" or "backing up" finds status matches alongside name/type matches.
    // Single _filter() call: the deletes above plus the assignment below settle dt.filters
    // before _filter reads them — the previous extra _filter() call was redundant and
    // doubled the cost on large datasets.
    dt.filters['global'] = { value: filterValue, matchMode: 'contains' }
    dt._filter()
    if (filterValue !== this.lastEmittedFilterText) {
      this.lastEmittedFilterText = filterValue
      this.filterTextChange.emit(filterValue)
    }

    // Restore sort state after applying filters
    if (currentSortField) {
      dt.sortField = currentSortField
      dt.sortOrder = currentSortOrder
      // Force PrimeNG to re-evaluate sort state
      setTimeout(() => {
        if (dt.sortField === currentSortField) {
          dt.sortSingle()
        }
      }, 0)
    }

    this.filterText = filterValue
    this.isFilterApplied = true

    // _rebuildAllFilteredChildren only iterates expanded groups, so syncAndAutoExpand
    // (called above) must have already toggled auto-expand state.
    this._rebuildAllFilteredChildren()
    this.loadingFilterInput = false
  }

  clear(table: Table) {
    this.isFilterApplied = false
    this.selectedCategories = []
    this.selectedStatuses = []
    this.selectedStatusFilters = []
    this.selectedResourceTypes = []
    this.selectedResourceGroups = []
    this.issuesFilterActive = false
    this.activeLetterFilter = null
    if (table) {
      // Clear filters while preserving sort state
      delete table.filters['resourceArn']
      delete table.filters['global']
      delete table.filters['status']
      table._filter()
      table.first = 0
      setTimeout(() => {
        table?.scrollTo({ top: 0 })
      }, 0)
    }
    // Clear the input field
    if (this.filterInput && this.filterInput.nativeElement) {
      this.filterInput.nativeElement.value = ''
    }
    // Rebuild expandable children to clear any text/filter narrowing
    this.filterText = ''
    this._rebuildAllFilteredChildren()
  }

  clearInput(table: Table) {
    // Only clear the text search — preserve active dropdown filters (status, type, group).
    // The old code called clear() which wiped all filter state.
    // Synchronous state updates so the user sees the input emptied immediately and the
    // spinner replaces the X icon. Heavy table work is deferred so the spinner paints first.
    this.filterText = ''
    this.isFilterApplied = false
    this.lastFilterEvent = null
    if (this.filterInput?.nativeElement) {
      this.filterInput.nativeElement.value = ''
    }
    if ('' !== this.lastEmittedFilterText) {
      this.lastEmittedFilterText = ''
      this.filterTextChange.emit('')
    }
    this.loadingFilterInput = true
    this.cdr.markForCheck()

    // Defer dt._filter() and _rebuildAllFilteredChildren — both can take seconds on
    // large k8s apps. rAF + setTimeout(0) lets Angular's post-event tick paint the
    // spinner first, and avoids the freezing seen with synchronous detectChanges() over
    // the open dropdown.
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (table) {
          delete table.filters['global']
          table._filter()
        }
        this.syncAndAutoExpand('')
        this._rebuildAllFilteredChildren()
        this.loadingFilterInput = false
        this.cdr.markForCheck()
      }, 0)
    })
  }

  clearFilterValues(dt1: Table, categoryToClear: string) {
    if (!categoryToClear) return

    delete dt1.filters['resourceArn']
    delete dt1.filters['status']
    dt1.filteredValue = null
    dt1.totalRecords = this.resources?.length || 0
    dt1.first = 0
    categoryToClear === 'selectedStatuses' ? (this.selectedStatuses = []) : (this.selectedCategories = [])
  }

  resourceTrackBy(index: number, resource: ViewableApplicationResource) {
    return resource.resourceArn
  }

  onIssuesClick(event: MouseEvent, resource: ViewableApplicationResource): void {
    event.stopPropagation()
    this.issuesClicked.emit(resource)
  }

  // Toggles the expandable detail row (resource properties, S3 config).
  // Cell text expansion is handled separately by toggleCellExpand() in onRowClick —
  // do NOT add cellExpandedRows logic here or it will double-toggle.
  toggleRow(resourceArn: string) {
    if (this.expandedRows.has(resourceArn)) {
      this.expandedRows.delete(resourceArn)
    } else {
      this.expandedRows.add(resourceArn)
      this.fetchResourceConfig(resourceArn)
    }
  }

  /** Click anywhere on a resource row to expand/collapse detail + cell truncation.
   *  Skips if the click target is a link, button, or copy element,
   *  or if the resource has no expandable content. */
  onRowClick(event: MouseEvent, resource: ViewableApplicationResource): void {
    // Don't toggle if clicking on interactive elements (links, buttons, copy icons)
    const target = event.target as HTMLElement
    if (target.closest('a, button, [copy-clipboard]')) return
    // Always toggle cell text expansion so truncated resource names can be revealed.
    // This must remain separate from the detail-row toggle below — removing it
    // breaks click-to-expand on rows without resource properties (most rows).
    this.toggleCellExpand(resource.resourceArn)
    // Only toggle the detail row if the resource has expandable content (properties, S3 config)
    if (this.hasExpandableContent(resource)) {
      this.toggleRow(resource.resourceArn)
    }
  }

  isS3Bucket(resource: ViewableApplicationResource) {
    return resource.resourceType == ResourceType.s3Bucket
  }

  /** Whether a resource has expandable content (properties or S3 replication mode). */
  hasExpandableContent(resource: ViewableApplicationResource): boolean {
    return resource?.resourceProperties?.length > 0 || this.isS3Bucket(resource)
  }

  fetchResourceConfig(resourceArn: string) {
    // Kick off background fetch of resource config
    const sub = this.application.getSyncPairResourceConfig$(resourceArn).subscribe(
      (resourceConfig) => {
        if (!!resourceConfig) {
          this.resourceConfigs.set(resourceArn, resourceConfig.resourceConfig)
        } else {
          this.resourceConfigs.set(resourceArn, null)
        }
        this.cdr.markForCheck() // Async subscription — OnPush needs explicit dirty mark
      },
      handleArpioError(window.location.href, null, this.uiService)
    )
    this.resourceConfigSubscriptions.push(sub)
  }

  isExpandedRow(resourceArn: string) {
    return this.expandedRows.has(resourceArn)
  }

  toggleCellExpand(resourceArn: string) {
    if (this.cellExpandedRows.has(resourceArn)) {
      this.cellExpandedRows.delete(resourceArn)
    } else {
      this.cellExpandedRows.add(resourceArn)
    }
  }

  isCellExpanded(resourceArn: string) {
    return this.cellExpandedRows.has(resourceArn)
  }

  onResourceNameCopied(resourceArn: string, column: 'source' | 'target') {
    this.copyFeedback.onCopied(`${resourceArn}:${column}`, this.cdr)
  }

  s3ReplicationMode(resource: ViewableApplicationResource) {
    // Returns string to display for the S3 bucket's replication mode

    if (this.resourceConfigs.has(resource.resourceArn)) {
      let s3Config = this.resourceConfigs.get(resource.resourceArn) as S3BucketConfig
      return S3_OBJECT_REPLICATION_MODES.get(s3Config?.objectReplicationMode)
    } else {
      // No entry in map, we are still waiting for it to be fetched from the server.
      return 'Loading...'
    }
  }

  get emptyText(): string {
    if (this.tabType === ResourcesTab.FAILBACK) {
      if (!!this.appStatus && this.appStatus.inProgress) {
        return 'Failback started, updating resource status...'
      } else if (this.application?.getRestoreResources()?.length > 0) {
        // Recovery resources exist but none passed the supportsFailback filter
        return 'No resources in this recovery point support failback.'
      } else {
        return 'Once you start a failback, resource status will appear here.'
      }
    } else if (this.isBackupContext) {
      // No selection rules = nothing configured. Match the headline instead of the
      // in-progress text — appStatus.inProgress can linger from a prior cycle's
      // DTO and reads as a lie when there are no resources at all.
      if (!this.application?.selectionRules?.length) {
        return 'No resources configured.'
      }
      if (!!this.appStatus && this.appStatus.inProgress) {
        return 'Backup in progress, updating resource status...'
      } else {
        return 'Backup status will appear here after your first backup completes.'
      }
    } else {
      // Recovery context
      if (!!this.appStatus && this.appStatus.inProgress) {
        return 'Restore in progress, updating resource status...'
      } else if (this.application && !this.application.lastBackupSuccess) {
        return this.application.currentlyBackingUp
          ? 'Your first backup is in progress. Resources will appear here once the first restore begins.'
          : 'Waiting for the first backup to complete before restoring to the recovery environment.'
      } else {
        return 'Recovery environment status will appear here after the next restore completes.'
      }
    }
  }

  // "Configure resources" link only makes sense on the Backup tab — configuring
  // resources controls what gets backed up, not what gets recovered.
  get showConfigureLink(): boolean {
    return this.isBackupContext && !this.loading && (!this.resources || this.resources.length === 0)
  }

  // True when resources have been configured (selectionRules exist) but haven't
  // appeared yet — shows an informative "discovering" message instead of skeleton rows.
  get resourcesConfiguredButEmpty(): boolean {
    return (
      this.loading &&
      !this._hasEverHadResources &&
      (!this.resources || this.resources.length === 0) &&
      !!this.application?.selectionRules?.length
    )
  }

  /**
   * Placeholder text for empty Recovery Resource cells.
   * Called when targetResourceName is empty (falsy).
   *
   * Empty states by status:
   *   Standby phase:
   *     "Not present during standby" — SUCCEEDED/QUARANTINED/SCAN_*: resource type is not maintained
   *                                    during standby (e.g. Transit Gateway Attachments).
   *   Non-standby phase:
   *     "Not present"       — SUCCEEDED/QUARANTINED/SCAN_*: restore completed but resource has no
   *                           corresponding target (e.g. half of a RAM share where the other side
   *                           isn't present, or a resource type that simply isn't created).
   *   "Not yet restored"   — NO_STATUS/NOT_PROCESSED/UNSATISFIED_DEPENDENCY: no recovery operation has
   *                           reached this resource yet. Either no job has run, the resource was skipped
   *                           because an upstream dependency failed, or it's blocked waiting on a dependency.
   *   "—" (em dash)        — PENDING/PROCESSING: a job is actively running but the action
   *                           could be creating or deleting depending on sync phase, so we show a
   *                           neutral placeholder rather than potentially misleading text.
   *   "Not restored"       — FAILED: the recovery operation attempted to create the target but failed.
   *   "—" (em dash)        — Should not occur in practice. All known statuses are covered above.
   *                           Acts as a safe fallback for any unexpected status value.
   */
  getRecoveryResourcePlaceholder(resource: ViewableApplicationResource): string {
    // Failback tab: use em dash for all empty cells. The descriptive recovery-context
    // strings ("Not present", "Not yet restored") don't apply since failback rows only
    // appear once a recovery environment exists, and the distinctions don't aid the user.
    if (this.isFailbackTab) return '—'
    switch (resource.status) {
      // Resource was processed (restore completed, scan ran, etc.) but no target exists in the
      // recovery region — the resource type doesn't create a corresponding target (e.g. Transit
      // Gateway Attachments during standby). "During standby" reassures the user it will appear
      // during an actual recovery.
      case ResourceStatus.SUCCEEDED:
      case ResourceStatus.QUARANTINED:
      case ResourceStatus.SCAN_CLEAN:
      case ResourceStatus.SCAN_INFECTED:
      case ResourceStatus.SCAN_FAILED:
        return this.application?.syncPhase === SyncPhaseDto.STANDBY ? 'Not present during standby' : 'Not present'
      case ResourceStatus.NO_STATUS:
      case ResourceStatus.NOT_PROCESSED:
      case ResourceStatus.UNSATISFIED_DEPENDENCY:
        return 'Not yet restored'
      case ResourceStatus.PENDING:
      case ResourceStatus.PROCESSING:
        return '—'
      case ResourceStatus.FAILED:
        return 'Not restored'
      default:
        return '—'
    }
  }

  /**
   * Placeholder text for empty Primary Resource (source) cells.
   * Called when sourceResourceName is empty (falsy).
   *
   * Empty states:
   *   "Deleted" — The application has been deleted. Source resources no longer exist.
   *   "—"      — Source resource data was not returned by the API (e.g. source was removed
   *              from the account or access was revoked). The ApplicationResource.sourceResourceName
   *              getter returns '' when sourceRes is null.
   */
  getPrimaryResourcePlaceholder(resource: ViewableApplicationResource): string {
    // Failback tab: always em dash, even for deleted apps (the "Deleted" label is a
    // recovery/backup-context concept; on failback an empty source just means nothing to return).
    if (this.isFailbackTab) return '—'
    if (this.appIsDeleted) return 'Deleted'
    return '—'
  }

  isPrimaryResourcePlaceholder(resource: ViewableApplicationResource): boolean {
    return !resource.sourceResourceName || resource.sourceResourceName === '(none)'
  }

  getIssueTypesTooltip(resource: ViewableApplicationResource): string | null {
    if (!resource.issues || resource.issues.size === 0) return null
    const types = Array.from(resource.issues)
      .map((i) => i.issue?.issueTypeDisplayString)
      .filter(Boolean)
    if (types.length === 0) return null
    return [...new Set(types)].join(', ')
  }

  // Filter dropdown functionality
  showFilterDropdown: boolean = false
  selectedStatusFilters: ResourceStatus[] = []
  // Filters shown as removable tags — hides NO_STATUS in recovery since it's merged with NOT_PROCESSED
  get displayedStatusFilters(): ResourceStatus[] {
    if (this.statusContext === StatusContext.RECOVERY) {
      return this.selectedStatusFilters.filter((s) => s !== ResourceStatus.NO_STATUS)
    }
    return this.selectedStatusFilters
  }
  selectedResourceTypes: string[] = []
  selectedResourceGroups: string[] = []
  issuesFilterActive: boolean = false
  expandedFilterSections: Record<string, boolean> = { status: true, resourceType: false, resourceGroup: false }
  @ViewChild('filterDropdown') filterDropdownRef!: ElementRef

  filterDropdownMaxHeight: string = null

  toggleFilterDropdown(): void {
    this.showFilterDropdown = !this.showFilterDropdown
    if (this.showFilterDropdown && this.filterDropdownRef) {
      // Cap dropdown height so it doesn't extend past the viewport bottom.
      // Measure from the bottom of the trigger button with a small margin.
      const rect = this.filterDropdownRef.nativeElement.getBoundingClientRect()
      const availableSpace = window.innerHeight - rect.bottom - 16 // 16px bottom margin
      this.filterDropdownMaxHeight = `${Math.max(200, availableSpace)}px`
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Early exit when dropdown is already closed to avoid unnecessary change detection
    if (!this.showFilterDropdown) return
    if (this.filterDropdownRef && !this.filterDropdownRef.nativeElement.contains(event.target)) {
      this.showFilterDropdown = false
    }
  }

  onStatusFilterChange(status: ResourceStatus, checked: boolean): void {
    if (checked) {
      if (!this.selectedStatusFilters.includes(status)) {
        this.selectedStatusFilters.push(status)
      }
    } else {
      this.selectedStatusFilters = this.selectedStatusFilters.filter((s) => s !== status)
    }
    this.applyStatusFilters()
  }

  toggleStatusFilter(status: ResourceStatus): void {
    const isActive = this.selectedStatusFilters.includes(status)
    // In recovery context, NOT_PROCESSED and NO_STATUS are treated as one filter — coalesce
    // both updates into a single applyStatusFilters call. The previous two-step path ran the
    // entire heavy filter pipeline twice per click, which compounded with the Filter-by
    // spinner's deferred work and froze the page on large k8s apps.
    if (status === ResourceStatus.NOT_PROCESSED && this.statusContext === StatusContext.RECOVERY) {
      if (isActive) {
        this.selectedStatusFilters = this.selectedStatusFilters.filter(
          (s) => s !== ResourceStatus.NOT_PROCESSED && s !== ResourceStatus.NO_STATUS
        )
      } else {
        const additions: ResourceStatus[] = []
        if (!this.selectedStatusFilters.includes(ResourceStatus.NOT_PROCESSED)) {
          additions.push(ResourceStatus.NOT_PROCESSED)
        }
        if (!this.selectedStatusFilters.includes(ResourceStatus.NO_STATUS)) {
          additions.push(ResourceStatus.NO_STATUS)
        }
        if (additions.length) {
          this.selectedStatusFilters = [...this.selectedStatusFilters, ...additions]
        }
      }
      this.applyStatusFilters()
      return
    }
    this.onStatusFilterChange(status, !isActive)
  }

  /** Set the status filter to show only the given status. Called from parent to filter to failed resources. */
  setStatusFilter(status: ResourceStatus): void {
    this.selectedStatusFilters = [status]
    this.applyStatusFilters()
  }

  // NOT_PROCESSED rows are downstream of a FAILED resource. Making the pill itself a link
  // (in addition to the banner up top) gives the user a direct jump from the row whose
  // status they're questioning. Gated on recovery context + a FAILED row actually existing
  // so we don't promise a jump that leads to an empty table.
  isNotProcessedLinkable(resource: ViewableApplicationResource): boolean {
    if (!resource || resource.status !== ResourceStatus.NOT_PROCESSED) return false
    if (this.statusContext !== StatusContext.RECOVERY) return false
    return (this._cachedStatusCounts.get(ResourceStatus.FAILED) || 0) > 0
  }

  // Click handler for linkable NOT_PROCESSED pills — filters the table to FAILED so the
  // user can see the root-cause resource(s) with their issue chips.
  onNotProcessedPillClick(_event: MouseEvent): void {
    this.setStatusFilter(ResourceStatus.FAILED)
  }

  toggleFilterSection(section: string): void {
    this.expandedFilterSections[section] = !this.expandedFilterSections[section]
  }

  toggleIssuesFilter(): void {
    this.issuesFilterActive = !this.issuesFilterActive
    this.applyColumnFilters()
  }

  getIssuesCount(): number {
    return this._cachedIssuesCount
  }

  toggleResourceTypeFilter(type: string): void {
    const index = this.selectedResourceTypes.indexOf(type)
    if (index >= 0) {
      this.selectedResourceTypes = this.selectedResourceTypes.filter((t) => t !== type)
    } else {
      this.selectedResourceTypes = [...this.selectedResourceTypes, type]
    }
    this.applyColumnFilters()
  }

  toggleResourceGroupFilter(group: string): void {
    const index = this.selectedResourceGroups.indexOf(group)
    if (index >= 0) {
      this.selectedResourceGroups = this.selectedResourceGroups.filter((g) => g !== group)
    } else {
      this.selectedResourceGroups = [...this.selectedResourceGroups, group]
    }
    this.applyColumnFilters()
  }

  removeResourceTypeFilter(type: string): void {
    this.selectedResourceTypes = this.selectedResourceTypes.filter((t) => t !== type)
    this.applyColumnFilters()
  }

  removeResourceGroupFilter(group: string): void {
    this.selectedResourceGroups = this.selectedResourceGroups.filter((g) => g !== group)
    this.applyColumnFilters()
  }

  getResourceTypeCount(type: string): number {
    return this._cachedResourceTypeCounts.get(type) || 0
  }

  getResourceGroupCount(group: string): number {
    return this._cachedResourceGroupCounts.get(group) || 0
  }

  // True when any dropdown pill filter (status, type, group, issues) is active
  private get hasActiveColumnFilters(): boolean {
    return (
      this.selectedStatusFilters.length > 0 ||
      this.selectedResourceTypes.length > 0 ||
      this.selectedResourceGroups.length > 0 ||
      this.issuesFilterActive
    )
  }

  get uniqueResourceGroups(): string[] {
    return this._cachedUniqueResourceGroups
  }

  private buildUniqueResourceGroups(): string[] {
    if (!this.resources) return []
    const groups = new Set<string>()
    this.resources.forEach((r) => {
      if (r.sourceResourceGroup) {
        groups.add(r.sourceResourceGroup)
      }
    })
    return Array.from(groups).sort()
  }

  get totalFilterCount(): number {
    return (
      this.selectedStatusFilters.length +
      this.selectedResourceTypes.length +
      this.selectedResourceGroups.length +
      (this.issuesFilterActive ? 1 : 0)
    )
  }

  private applyStatusFilters(): void {
    this.applyColumnFilters()
  }

  /**
   * Reset ALL PrimeNG table filters including global search.
   * Used by clearAllFilters() which also resets the search input.
   */
  private resetTableFilters(): void {
    if (!this.resourcesTable) return
    delete this.resourcesTable.filters['resourceArn']
    delete this.resourcesTable.filters['global']
    delete this.resourcesTable.filters['status']
    this.resourcesTable._filter()
    this.resourcesTable.first = 0
  }

  /**
   * Reset only column-specific filters (status, resourceArn) while preserving
   * the global text search. Used when removing individual filter pills so the
   * active search isn't silently cleared.
   */
  private resetColumnFilters(): void {
    if (!this.resourcesTable) return
    delete this.resourcesTable.filters['resourceArn']
    delete this.resourcesTable.filters['status']
    this.resourcesTable._filter()
    this.resourcesTable.first = 0
  }

  /**
   * Set filter metadata on the PrimeNG table and immediately run _filter().
   * This bypasses PrimeNG's 300ms filterDelay to ensure instant, reliable filtering.
   */
  private setTableFilter(value: any[], field: string, matchMode: string): void {
    if (!this.resourcesTable) return
    // Cancel any pending PrimeNG filter timeout
    if (this.resourcesTable.filterTimeout) {
      clearTimeout(this.resourcesTable.filterTimeout)
      this.resourcesTable.filterTimeout = null
    }
    this.resourcesTable.filters[field] = { value, matchMode }
    this.resourcesTable._filter()
  }

  // Applies all active pill filters using AND across filter types, OR within each type.
  // K8s resources are filtered separately since they live outside PrimeNG's [value] pipeline.
  private applyColumnFilters(): void {
    if (!this.resourcesTable) return

    // Show a spinner on the Filter by button across the heavy pipeline. Avoid detectChanges()
    // here — pill clicks happen with the dropdown open, and a synchronous CD pass over the
    // open dropdown's hundreds of pill bindings (isPillActive / getPillCount /
    // isPillVisibleForLetter per pill) freezes the click handler. markForCheck lets Angular's
    // post-event tick paint the spinner; rAF + setTimeout(0) defers the heavy work past the
    // paint so the spinner is visible before the filter pipeline blocks the UI thread.
    this.loadingFilterBy = true
    this.cdr.markForCheck()
    requestAnimationFrame(() => {
      setTimeout(() => {
        this._runApplyColumnFilters()
        this.loadingFilterBy = false
        this.cdr.markForCheck()
      }, 0)
    })
  }

  private _runApplyColumnFilters(): void {
    if (!this.resourcesTable) return

    const hasStatusFilters = this.selectedStatusFilters.length > 0
    const hasTypeFilters = this.selectedResourceTypes.length > 0
    const hasGroupFilters = this.selectedResourceGroups.length > 0
    const hasIssuesFilter = this.issuesFilterActive

    if (!hasStatusFilters && !hasTypeFilters && !hasGroupFilters && !hasIssuesFilter) {
      // Only clear column-specific filters — preserve global search if active.
      // resetTableFilters() also deletes filters['global'], which would silently
      // kill the text search when removing the last column filter pill.
      this.resetColumnFilters()
      // If a pill removal left us with no column filters, collapse any groups we auto-expanded
      // when those filters were applied so the view returns to its pre-filter shape. Re-run the
      // text-based auto-expand afterwards in case the active search still matches descendants —
      // that path sets autoExpanded itself and must not be clobbered.
      for (const group of this.expandableGroups.values()) {
        if (group.autoExpanded) {
          group.expanded = false
          group.autoExpanded = false
          group.filteredChildren = []
        }
      }
      if (this.filterText) {
        this.syncAndAutoExpand(this.filterText)
      }
    } else {
      // AND across filter types, OR within each type:
      // e.g. (status=Failed OR status=Healthy) AND (type=EC2 OR type=RDS)
      const matchingResources = this.resources.filter((r) => {
        const matchesStatus = hasStatusFilters ? this.selectedStatusFilters.includes(r.status) : true
        const matchesType = hasTypeFilters ? this.selectedResourceTypes.includes(r.displayResourceType) : true
        const matchesGroup = hasGroupFilters ? this.selectedResourceGroups.includes(r.sourceResourceGroup || '') : true
        const matchesIssues = hasIssuesFilter ? r.hasIssues : true
        return matchesStatus && matchesType && matchesGroup && matchesIssues
      })

      // Split matches into display rows (for PrimeNG) and expandable children (for parent groups).
      // Children aren't in displayRows, so their ARNs won't match PrimeNG's filter.
      // Instead, include the TOP-LEVEL ancestor's ARN so it stays visible when a descendant matches.
      const childArns = new Set<string>()
      for (const group of this.expandableGroups.values()) {
        for (const child of group.rawChildren) {
          childArns.add(child.resourceArn)
        }
      }

      // EKS nests multiple levels deep (Cluster → Namespace → Pod) — only the top-level
      // Cluster lands in displayRows. A Pod's parentArn points to its Namespace (which
      // is itself a child), so pushing the immediate parent doesn't help PrimeNG find a
      // visible row. Walk up until we reach a resource whose parent isn't in the set,
      // which is the top-level ancestor rendered in displayRows.
      const parentArnByArn = new Map<string, string>()
      const arnsInResources = new Set<string>()
      for (const r of this.resources) {
        arnsInResources.add(r.resourceArn)
        if (r.parentArn) parentArnByArn.set(r.resourceArn, r.parentArn)
      }
      const findTopLevelAncestor = (arn: string): string => {
        let current = arn
        const visited = new Set<string>()
        while (parentArnByArn.has(current) && arnsInResources.has(parentArnByArn.get(current)!)) {
          if (visited.has(current)) break // defensive — shouldn't happen
          visited.add(current)
          current = parentArnByArn.get(current)!
        }
        return current
      }

      const tableArns: string[] = []
      const pushedAncestors = new Set<string>()
      // Track which top-level parents survive the filter only because a descendant matched
      // (the parent itself didn't match). Those are candidates for auto-expand so the user
      // immediately sees the matching descendants inside the visible parent row.
      const ancestorsFromDescendantMatch = new Set<string>()
      const parentMatchedDirectly = new Set<string>()
      for (const r of matchingResources) {
        if (childArns.has(r.resourceArn)) {
          // Descendant match — surface its top-level ancestor (single-level or deeper).
          const ancestor = findTopLevelAncestor(r.resourceArn)
          ancestorsFromDescendantMatch.add(ancestor)
          if (!pushedAncestors.has(ancestor)) {
            pushedAncestors.add(ancestor)
            tableArns.push(ancestor)
          }
        } else {
          if (this.expandableGroups.has(r.resourceArn)) {
            parentMatchedDirectly.add(r.resourceArn)
          }
          tableArns.push(r.resourceArn)
        }
      }

      // Auto-expand groups whose ONLY matches are descendants (parent didn't match directly).
      // Respect user intent: don't collapse a group the user already expanded manually — only
      // groups we auto-expand here get `autoExpanded = true` so clearAllFilters can restore
      // the prior state without touching manually-expanded ones.
      for (const ancestor of ancestorsFromDescendantMatch) {
        if (parentMatchedDirectly.has(ancestor)) continue
        const group = this.expandableGroups.get(ancestor)
        if (!group || group.expanded) continue
        group.expanded = true
        group.autoExpanded = true
        this._initGroupChildren(group)
      }

      // Clean any stale filter entries, then set our filter and run immediately
      delete this.resourcesTable.filters['status']
      if (tableArns.length === 0) {
        // PrimeNG's 'in' filter treats empty arrays as "no filter" (shows all rows),
        // so handle the empty case by setting filteredValue directly.
        delete this.resourcesTable.filters['resourceArn']
        this.resourcesTable.filteredValue = []
        this.resourcesTable.totalRecords = 0
        this.resourcesTable.first = 0
      } else {
        this.setTableFilter(tableArns, 'resourceArn', 'in')
      }
    }
    this._rebuildAllFilteredChildren()
  }

  removeStatusFilter(status: ResourceStatus): void {
    // In recovery context, removing NOT_PROCESSED also removes NO_STATUS
    if (status === ResourceStatus.NOT_PROCESSED && this.statusContext === StatusContext.RECOVERY) {
      this.selectedStatusFilters = this.selectedStatusFilters.filter(
        (s) => s !== ResourceStatus.NOT_PROCESSED && s !== ResourceStatus.NO_STATUS
      )
    } else {
      this.selectedStatusFilters = this.selectedStatusFilters.filter((s) => s !== status)
    }
    this.applyColumnFilters()
  }

  clearAllFilters(): void {
    this.selectedStatusFilters = []
    this.selectedResourceTypes = []
    this.selectedResourceGroups = []
    this.issuesFilterActive = false
    this.activeLetterFilter = null
    this.filterText = ''
    if (this.filterInput?.nativeElement) {
      this.filterInput.nativeElement.value = ''
    }
    // Emit clear to parent so it persists across tabs
    if (this.lastEmittedFilterText !== '') {
      this.lastEmittedFilterText = ''
      this.filterTextChange.emit('')
    }
    // Reset per-group issues filter on expandable parents.
    // Collapse ONLY groups that were auto-expanded by a filter match — leave groups the user
    // opened manually in their current state so clearing filters doesn't fight their intent.
    for (const group of this.expandableGroups.values()) {
      group.issuesOnly = false
      // Bypass is anchored to global filter state — clearing all filters means there's nothing
      // to bypass anymore, so reset it too.
      group.bypassGlobalFilters = false
      if (group.autoExpanded) {
        group.expanded = false
        group.autoExpanded = false
        group.filteredChildren = []
      }
    }
    if (this.resourcesTable) {
      this.resetTableFilters()
      setTimeout(() => {
        this.resourcesTable?.scrollTo({ top: 0 })
      }, 0)
    }
    this._rebuildAllFilteredChildren()
    this.isFilterApplied = false
  }

  // Cached as getter to avoid re-evaluation on every change detection cycle
  // (called 3x in template: clear-all button, filter tags, empty state)
  get hasActiveFilters(): boolean {
    return (
      this.selectedStatusFilters.length > 0 ||
      this.selectedResourceTypes.length > 0 ||
      this.selectedResourceGroups.length > 0 ||
      this.issuesFilterActive ||
      this.isFilterApplied
    )
  }

  getStatusLabel(status: ResourceStatus): string {
    const labels = this.statusContext === StatusContext.BACKUP ? BACKUP_STATUS_LABELS : RECOVERY_STATUS_LABELS
    return labels[status] || 'Unknown'
  }

  getStatusCount(status: ResourceStatus): number {
    return this._cachedStatusCounts.get(status) || 0
  }

  /** Count of resources skipped because an upstream dependency failed. Excludes pilot-light rows. */
  get upstreamBlockedCount(): number {
    return this._cachedUpstreamBlockedCount
  }

  /** True when the user should see a notice connecting NOT_PROCESSED rows to the failed upstream.
   *  Hidden while the status filter is already set to FAILED — the user is already there. */
  get showUpstreamFailureNotice(): boolean {
    if (this.statusContext !== StatusContext.RECOVERY) return false
    if (this._cachedUpstreamBlockedCount === 0) return false
    if ((this._cachedStatusCounts.get(ResourceStatus.FAILED) || 0) === 0) return false
    const onlyFailed =
      this.selectedStatusFilters.length === 1 && this.selectedStatusFilters[0] === ResourceStatus.FAILED
    return !onlyFailed
  }

  get statusSummaryTooltip(): string {
    const summary = this.statusSummary
    const parts: string[] = []
    if (summary.succeeded > 0) parts.push(`${summary.succeeded} Succeeded`)
    if (summary.processing > 0) parts.push(`${summary.processing} Processing`)
    if (summary.pending > 0) parts.push(`${summary.pending} Pending`)
    if (summary.blocked > 0) parts.push(`${summary.blocked} Blocked`)
    if (summary.notProcessed > 0) parts.push(`${summary.notProcessed} Not Processed`)
    if (summary.failed > 0) parts.push(`${summary.failed} Failed`)
    return parts.length > 0 ? parts.join(' · ') : 'No resources'
  }

  get shouldShowResourceGroupColumn() {
    return this._shouldShowResourceGroupColumn
  }

  get globalFilterFields() {
    return this._globalFilterFields
  }

  get notEmptyList() {
    return this._notEmptyList
  }

  get isFailbackTab() {
    return this.tabType === ResourcesTab.FAILBACK
  }

  get isBackupContext() {
    return this.statusContext === StatusContext.BACKUP
  }

  // Mirrors isBackupContext as a host class so SCSS can give Backup-only column overrides
  // (e.g. wider Resource Group column when Recovery Resource isn't competing for space).
  @HostBinding('class.is-backup-context') get backupContextHostClass() {
    return this.isBackupContext
  }

  get isRecoveryContext() {
    return this.statusContext === StatusContext.RECOVERY
  }

  // Mirrors isRecoveryContext as a host class for Recovery-only column overrides.
  @HostBinding('class.is-recovery-context') get recoveryContextHostClass() {
    return this.isRecoveryContext
  }

  /**
   * Compute summary statistics for the current resources.
   */
  get statusSummary() {
    return this._cachedStatusSummary
  }

  private buildStatusSummary() {
    if (!this.resources) {
      return { total: 0, succeeded: 0, failed: 0, processing: 0, pending: 0, blocked: 0, notProcessed: 0 }
    }

    const filtered = this.resourcesTable?.filteredValue || this.resources
    let succeeded = 0,
      failed = 0,
      processing = 0,
      pending = 0,
      blocked = 0,
      notProcessed = 0
    for (const r of filtered) {
      switch (r.status) {
        case ResourceStatus.SUCCEEDED:
          succeeded++
          break
        case ResourceStatus.FAILED:
          failed++
          break
        case ResourceStatus.PROCESSING:
          processing++
          break
        case ResourceStatus.PENDING:
          pending++
          break
        case ResourceStatus.UNSATISFIED_DEPENDENCY:
          blocked++
          break
        // In recovery context, NO_STATUS and NOT_PROCESSED both represent "Not Processed".
        // Only merge in recovery context — in backup context NO_STATUS is a distinct state.
        case ResourceStatus.NO_STATUS:
          if (this.statusContext === StatusContext.RECOVERY) {
            notProcessed++
          }
          break
        case ResourceStatus.NOT_PROCESSED:
          notProcessed++
          break
      }
    }
    return { total: filtered.length, succeeded, failed, processing, pending, blocked, notProcessed }
  }

  /** Pre-compute all pill counts in a single pass over resources. Called from ngOnChanges
   *  when resources or statusContext change, so template reads are O(1) lookups. */
  private rebuildPillCounts(): void {
    const statusCounts = new Map<ResourceStatus, number>()
    const typeCounts = new Map<string, number>()
    const groupCounts = new Map<string, number>()
    let issuesCount = 0
    let upstreamBlockedCount = 0

    if (this.resources) {
      for (const r of this.resources) {
        // Status counts — merge NO_STATUS into NOT_PROCESSED in recovery context
        const countStatus =
          r.status === ResourceStatus.NO_STATUS && this.statusContext === StatusContext.RECOVERY
            ? ResourceStatus.NOT_PROCESSED
            : r.status
        statusCounts.set(countStatus, (statusCounts.get(countStatus) || 0) + 1)

        // Resource type counts
        if (r.displayResourceType) {
          typeCounts.set(r.displayResourceType, (typeCounts.get(r.displayResourceType) || 0) + 1)
        }

        // Resource group counts
        const group = r.sourceResourceGroup || ''
        if (group) {
          groupCounts.set(group, (groupCounts.get(group) || 0) + 1)
        }

        // Issues count
        if (r.hasIssues) issuesCount++

        // Upstream-blocked count — matches the "Not Processed" filter chip count.
        // In recovery context, resource-viewer-base remaps pilot lights to SUCCEEDED, so
        // NO_STATUS here means stale/never-processed (effectively blocked by upstream failure).
        if (
          r.status === ResourceStatus.NOT_PROCESSED ||
          (r.status === ResourceStatus.NO_STATUS && this.statusContext === StatusContext.RECOVERY)
        ) {
          upstreamBlockedCount++
        }
      }
    }

    this._cachedStatusCounts = statusCounts
    this._cachedResourceTypeCounts = typeCounts
    this._cachedResourceGroupCounts = groupCounts
    this._cachedIssuesCount = issuesCount
    this._cachedUpstreamBlockedCount = upstreamBlockedCount
  }

  /** Pre-compute per-resource display values so the template uses Map lookups
   *  instead of method calls that re-execute every change detection cycle. */
  private rebuildPerResourceCaches(): void {
    // Include all expandable children alongside regular resources for cache building
    const expandableChildren: ViewableApplicationResource[] = []
    for (const group of this.expandableGroups.values()) {
      expandableChildren.push(...(group.children.length > 0 ? group.children : group.rawChildren))
    }
    const allResources = [...(this.resources || []), ...expandableChildren]
    const tooltips = new Map<string, string | null>()
    const recoveryPlaceholders = new Map<string, string>()
    const primaryIsPlaceholder = new Map<string, boolean>()
    const primaryPlaceholderText = new Map<string, string>()

    // _isLinkable is stamped here so the template can read it as a property instead of calling
    // isNotProcessedLinkable(...) per row × per CD pass — the method-call form was evaluating
    // 5000+ times per hover-CD on filtered EKS apps and dominated CD cost. Recomputed whenever
    // resources/statusContext/pill counts change (rebuildPillCounts runs first).
    const failedCount = this._cachedStatusCounts.get(ResourceStatus.FAILED) || 0
    const linkableContext = this.statusContext === StatusContext.RECOVERY && failedCount > 0

    for (const r of allResources) {
      const key = r.resourceArn
      // Issue types tooltip
      if (r.issues && r.issues.size > 0) {
        const types = Array.from(r.issues)
          .map((i: any) => i.issue?.issueTypeDisplayString)
          .filter(Boolean)
        tooltips.set(key, types.length > 0 ? [...new Set(types)].join(', ') : null)
      } else {
        tooltips.set(key, null)
      }
      // Recovery resource placeholder
      recoveryPlaceholders.set(key, this.getRecoveryResourcePlaceholder(r))
      // Primary resource placeholder
      const isPrimPlaceholder = !r.sourceResourceName || r.sourceResourceName === '(none)'
      primaryIsPlaceholder.set(key, isPrimPlaceholder)
      primaryPlaceholderText.set(key, this.getPrimaryResourcePlaceholder(r))
      // Linkable NOT_PROCESSED pill: stamped per-row to avoid the method-call cost in templates
      ;(r as any)._isLinkable = linkableContext && r.status === ResourceStatus.NOT_PROCESSED
      // Stamps for per-CD allocations the templates used to do inline (8000 children × CD = bad)
      ;(r as any)._portalTooltip = 'View in ' + (r.resourcePortalName || 'portal')
      ;(r as any)._copySourceKey = key + ':source'
      ;(r as any)._copyTargetKey = key + ':target'
      // _statusLabel is what _rebuildFilteredChildren and the global-filter pipeline match against;
      // stamping on every resource (including children) avoids re-deriving the label per filter pass.
      ;(r as any)._statusLabel = this.getStatusLabel(r.status)
    }

    this._cachedIssueTooltips = tooltips
    this._cachedRecoveryPlaceholders = recoveryPlaceholders
    this._cachedPrimaryIsPlaceholder = primaryIsPlaceholder
    this._cachedPrimaryPlaceholderText = primaryPlaceholderText
  }

  /**
   * Check if any resources are actively processing.
   */
  get hasActiveProcessing(): boolean {
    return this.statusSummary.processing > 0 || this.statusSummary.pending > 0
  }

  /**
   * Check if there are any issues (failed or blocked resources).
   */
  get hasIssues(): boolean {
    return this.statusSummary.failed > 0 || this.statusSummary.blocked > 0
  }

  /**
   * Compute overall progress across all resources.
   * Returns a value between 0 and 100.
   */
  get overallProgress(): number {
    if (!this.resources || this.resources.length === 0) return 0

    const total = this.resources.length
    const completed = this.resources.filter(
      (r) => r.status === ResourceStatus.SUCCEEDED || r.status === ResourceStatus.FAILED
    ).length

    // For resources in progress, count their individual progress
    const inProgressResources = this.resources.filter((r) => r.status === ResourceStatus.PROCESSING)
    const inProgressSum = inProgressResources.reduce((sum, r) => sum + (r.progress || 0), 0)

    // Calculate weighted progress
    const completedWeight = completed
    const inProgressWeight = inProgressSum
    const totalProgress = ((completedWeight + inProgressWeight) / total) * 100

    return Math.round(totalProgress)
  }

  /**
   * Get a human-readable progress message.
   */
  get progressMessage(): string {
    const summary = this.statusSummary
    const actionWord = this.isBackupContext ? 'Backing up' : 'Restoring'

    if (summary.processing > 0) {
      return `${actionWord} ${summary.processing} resource${summary.processing > 1 ? 's' : ''}...`
    } else if (summary.pending > 0) {
      return `${summary.pending} resource${summary.pending > 1 ? 's' : ''} queued`
    }
    return ''
  }

  /**
   * Export the current filtered resources to CSV.
   */

  // Exports filtered (or all) resources as CSV. Columns adapt to context (backup vs recovery) and cloud provider.
  exportToCsv(): void {
    const dataToExport = this.resourcesTable?.filteredValue || this.resources
    if (!dataToExport || dataToExport.length === 0) return

    const labels = this.statusContext === StatusContext.BACKUP ? BACKUP_STATUS_LABELS : RECOVERY_STATUS_LABELS

    // Build CSV headers based on context
    const headers = ['Status', 'Resource Type']
    if (this._shouldShowResourceGroupColumn && !this.appIsDeleted) {
      headers.push('Resource Group')
    }
    if (!this.appIsDeleted) {
      headers.push('Primary Resource')
    }
    if (this.isRecoveryContext) {
      headers.push('Recovery Resource')
    }
    // Cloud-aware identifier column headers: "ARN" for AWS, "Resource ID" for Azure
    const isAzure = this.application?.type === ApplicationType.AZURE
    const idLabel = isAzure ? 'Resource ID' : 'ARN'
    headers.push(`Source ${idLabel}`)
    if (this.isRecoveryContext) {
      headers.push(`Recovery ${idLabel}`)
    }

    // Build CSV rows
    const rows = dataToExport.map((resource) => {
      const row: string[] = []
      row.push(labels[resource.status] || 'Unknown')
      row.push(resource.displayResourceType)

      if (this._shouldShowResourceGroupColumn && !this.appIsDeleted) {
        row.push(resource.sourceResourceGroup || '')
      }
      if (!this.appIsDeleted) {
        row.push(resource.sourceResourceName || '')
      }
      if (this.isRecoveryContext) {
        row.push(resource.targetResourceName || '')
      }
      row.push(resource.resourceArn || '')
      if (this.isRecoveryContext) {
        row.push(resource.targetResourceArn || '')
      }

      return row
    })

    // Generate CSV content with BOM for proper Excel encoding
    const csvContent =
      '\uFEFF' +
      [headers, ...rows].map((row) => row.map((cell) => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n')

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const contextName = this.isBackupContext ? 'backups' : 'recovery'
    const appName = this.application?.name ? this.application.name.replace(/[^a-zA-Z0-9-_]/g, '_') : 'resources'
    const timestamp = new Date().toISOString().split('T')[0]
    link.href = URL.createObjectURL(blob)
    link.download = `${appName}-${contextName}-${timestamp}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  /**
   * Format a timestamp for display in the table.
   * Returns a relative time string (e.g., "2 hours ago") or absolute date for older timestamps.
   */
  formatTimestamp(timestamp: Date | undefined): string {
    if (!timestamp) {
      return '—'
    }

    const now = new Date()
    const date = new Date(timestamp)
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) {
      return 'Just now'
    } else if (diffMins < 60) {
      return `${diffMins} min ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else {
      // For older timestamps, show the date
      return date.toLocaleDateString()
    }
  }

  /**
   * Calculate backup age as a human-readable string.
   */
  getBackupAge(timestamp: Date | undefined): string {
    return this.formatTimestamp(timestamp)
  }

  // ── Expandable parent-child resource grouping ──

  /** Total visible column count — used for expandable parent row colspan.
   *  Status + Resource Type + Primary Resource always render; Resource Group shows
   *  for Azure apps and Recovery Resource shows on Recovery scope. */
  get subheadingColspan(): number {
    let cols = 3 // Status + Resource Type + Primary Resource
    if (this._shouldShowResourceGroupColumn) cols++
    if (this.isRecoveryContext) cols++ // Recovery Resource
    return cols
  }

  /**
   * Compute the "Displaying X" count for the footer summary.
   * Expandable children are rendered outside PrimeNG's [value] pipeline, so the
   * filtered row count must include visible children alongside displayRows.
   */
  getDisplayedCount(table: any): number {
    const baseCount = table.filteredValue?.length ?? this.displayRows?.length ?? 0
    if (this.expandableGroups.size === 0) return baseCount

    // Add visible child counts from expanded groups
    let childCount = 0
    for (const group of this.expandableGroups.values()) {
      if (!group.expanded) continue
      // Check if the parent row survived PrimeNG's filter
      const filteredRows = table.filteredValue ?? this.displayRows
      const parentVisible = filteredRows?.some((r: any) => r.resourceArn === group.parentArn) ?? false
      if (!parentVisible) continue
      childCount += group.initialized ? (group.filteredChildren?.length ?? 0) : group.childCount
    }
    return baseCount + childCount
  }

  buildDisplayRows(): void {
    if (!this.resources || this.resources.length === 0) {
      this.displayRows = this.resources || []
      this.expandableGroups = new Map()
      return
    }

    // Group children by IMMEDIATE parentArn first — this gives us a tree edge list.
    // For EKS the tree nests multiple levels: Cluster → Namespace → Pod. Namespace is a
    // child of Cluster and ALSO a parent of Pods. Only TOP-LEVEL ancestors (whose parent is
    // missing/not in the list) render as expandable parent rows; everything else is flattened
    // into the top-level group's children so Pods are visible when the Cluster is expanded
    // and the faceted pill's status counts reflect all descendants, not just immediate ones.
    const parentArns = new Set(this.resources.map((r) => r.resourceArn))
    const immediateChildrenByParent = new Map<string, ViewableApplicationResource[]>()
    const regularRows: ViewableApplicationResource[] = []

    for (const r of this.resources) {
      if (r.parentArn && parentArns.has(r.parentArn)) {
        // This resource has a parent in the list — group it under its immediate parent
        const list = immediateChildrenByParent.get(r.parentArn) || []
        list.push(r)
        immediateChildrenByParent.set(r.parentArn, list)
      } else {
        // Top-level: no parent, or parent not in list (orphan). These render as regular rows.
        regularRows.push(r)
      }
    }

    // No parent-child relationships found — use resources as-is
    if (immediateChildrenByParent.size === 0) {
      this._stampStatusLabels(this.resources)
      this.displayRows = this.resources
      this.expandableGroups = new Map()
      return
    }

    // Recursively flatten all descendants under each top-level parent so deep hierarchies
    // (Cluster → Namespace → Pod) collapse into one expandable group. Without this, grandchildren
    // never render (child rows don't support nested expand) and the Cluster's facet pill misses
    // any status that only exists on grandchildren.
    const collectDescendants = (parentArn: string): ViewableApplicationResource[] => {
      const out: ViewableApplicationResource[] = []
      const queue: string[] = [parentArn]
      const visited = new Set<string>()
      // Index pointer instead of Array.shift() — shift is O(n), so a 7000-pod BFS was O(n²).
      let head = 0
      while (head < queue.length) {
        const current = queue[head++]
        if (visited.has(current)) continue // defensive — shouldn't happen in a tree
        visited.add(current)
        const kids = immediateChildrenByParent.get(current)
        if (!kids) continue
        for (const kid of kids) {
          out.push(kid)
          queue.push(kid.resourceArn)
        }
      }
      return out
    }

    // Build or update ExpandableChildGroup for each TOP-LEVEL parent, preserving expand state
    const newGroups = new Map<string, ExpandableChildGroup>()
    for (const topLevelParent of regularRows) {
      // Skip rows that have no descendants — they're leaf resources, not parents.
      if (!immediateChildrenByParent.has(topLevelParent.resourceArn)) continue

      const pArn = topLevelParent.resourceArn
      const descendants = collectDescendants(pArn)
      const existing = this.expandableGroups.get(pArn)

      // Single descendant pass: needs-attention flag, issue count, and in-progress rollup.
      // Needs-attention trigger states (failed / blocked / not-processed / scan failures) flip
      // the rollup pill. PROCESSING/PENDING descendants under a "settled" parent (e.g. a cluster
      // that reports SUCCESS while pods are still restoring) flip rollupInProgressStatus so the
      // parent pill reflects the in-flight work rather than reading as "done".
      // Tooltip is intentionally framed as "children with issues" rather than per-status counts —
      // a child marked FAILED here is usually a cascade symptom of the parent's own state, so
      // surfacing "1 Failed" reads as if a child failed in isolation. The per-row issue icons
      // are the drill-down path for users who want detail.
      const triggerSet = this._needsAttentionTriggerSet()
      let needsAttention = false
      let issueCount = 0
      let rollupInProgressStatus: ResourceStatus | null = null
      for (const r of descendants) {
        if (triggerSet.has(r.status)) needsAttention = true
        if (r.hasIssues) issueCount++
        if (r.status === ResourceStatus.PROCESSING) {
          rollupInProgressStatus = ResourceStatus.PROCESSING
        } else if (r.status === ResourceStatus.PENDING && rollupInProgressStatus !== ResourceStatus.PROCESSING) {
          rollupInProgressStatus = ResourceStatus.PENDING
        }
      }
      const needsAttentionTooltip = this._formatNeedsAttentionTooltip(issueCount)

      const group: ExpandableChildGroup = {
        parentArn: pArn,
        rawChildren: descendants,
        children: existing?.initialized ? descendants : [],
        filteredChildren: [],
        initialized: false,
        expanded: existing?.expanded ?? false,
        loading: false,
        autoExpanded: existing?.autoExpanded ?? false,
        needsAttention,
        needsAttentionTooltip,
        rollupInProgressStatus,
        issueCount,
        childCount: descendants.length,
        issuesOnly: existing?.issuesOnly ?? false,
        bypassGlobalFilters: existing?.bypassGlobalFilters ?? false,
        applyingIssues: false,
      }

      // If the group was already expanded, re-initialize children immediately
      if (group.expanded) {
        this._initGroupChildren(group)
      }

      newGroups.set(pArn, group)
    }
    this.expandableGroups = newGroups

    // Mark parent rows that have children so the template can render expand toggles.
    // Attach child metadata directly on the row object for template access.
    for (const row of regularRows) {
      const group = this.expandableGroups.get(row.resourceArn)
      if (group) {
        ;(row as any)._hasExpandableChildren = true
        ;(row as any)._childCount = group.childCount
        ;(row as any)._childIssueCount = group.issueCount
        ;(row as any)._needsAttention = this._resolveParentNeedsAttention(row.status, group.needsAttention)
        // Promote the parent's pill status when children are still in-flight under a settled
        // parent — e.g. an EKS cluster that already reads SUCCESS while pods are still restoring
        // should display as Restoring/Backing Up. Skipped when the parent's own status is itself
        // a needs-attention trigger (FAILED stays FAILED) or already at-or-past the rollup.
        ;(row as any)._displayStatus = this._resolveParentDisplayStatus(row.status, group.rollupInProgressStatus)
        // Back-ref so filter passes can update _childIssueCount without scanning displayRows.
        group.parentRow = row
      }
    }

    this._stampStatusLabels(regularRows)
    this.displayRows = regularRows
    // Rebuild filtered children for all expanded groups
    this._rebuildAllFilteredChildren()
  }

  // Inject a searchable _statusLabel string on each row so PrimeNG's global filter
  // can match status text (e.g. "backing up", "ready") alongside resource names.
  private _stampStatusLabels(rows: ViewableApplicationResource[]): void {
    for (const r of rows) {
      ;(r as any)._statusLabel = this.getStatusLabel(r.status)
    }
  }

  // Trigger set for the active context — drives both the rollup pill and the click-through filter.
  private _needsAttentionTriggerSet(): Set<ResourceStatus> {
    return this.statusContext === StatusContext.BACKUP
      ? NEEDS_ATTENTION_STATUSES_BACKUP
      : NEEDS_ATTENTION_STATUSES_RECOVERY
  }

  // PROCESSING outranks PENDING outranks any settled state (SUCCEEDED, scan results, etc.).
  // Trigger states (FAILED, NOT_PROCESSED, ...) are intentionally rank 0 so they're never
  // overwritten by an in-progress rollup — a FAILED parent stays FAILED even if a sibling pod
  // is still processing.
  private _inProgressRank(status: ResourceStatus | null): number {
    if (status === ResourceStatus.PROCESSING) return 2
    if (status === ResourceStatus.PENDING) return 1
    return 0
  }

  // Promote the parent's pill to the descendant rollup status only when (a) the parent is not
  // itself in a needs-attention trigger and (b) the rollup is more advanced than the parent's
  // own status. Returns the parent's own status when no override applies.
  private _resolveParentDisplayStatus(
    parentStatus: ResourceStatus,
    rollupInProgressStatus: ResourceStatus | null
  ): ResourceStatus {
    if (rollupInProgressStatus === null) return parentStatus
    if (this._needsAttentionTriggerSet().has(parentStatus)) return parentStatus
    if (this._inProgressRank(rollupInProgressStatus) > this._inProgressRank(parentStatus)) {
      return rollupInProgressStatus
    }
    return parentStatus
  }

  // Whether the parent row should show the descendant rollup ("Needs attention") pill. Suppress it
  // when the parent's own status is itself a trigger state (e.g. a failed AKS cluster) so its real,
  // more specific status pill shows through instead of a generic rollup — mirrors the "FAILED parent
  // stays FAILED" rule in _resolveParentDisplayStatus. group.needsAttention stays the honest
  // descendant-only rollup; this only gates the per-row display.
  private _resolveParentNeedsAttention(parentStatus: ResourceStatus, descendantsNeedAttention: boolean): boolean {
    return descendantsNeedAttention && !this._needsAttentionTriggerSet().has(parentStatus)
  }

  // Tooltip phrasing for the "Needs attention" pill. Strict hasIssues count, no status breakdown:
  // user feedback is that "1 Failed" reads as a child failure when it's usually a cascade symptom
  // of the parent's own state — generic "with issues" wording defers detail to the per-row icons.
  // 0 issues → empty string, which the template treats as "no tooltip".
  private _formatNeedsAttentionTooltip(issueCount: number): string {
    if (issueCount === 0) return ''
    return `${issueCount} ${issueCount === 1 ? 'child' : 'children'} with issues`
  }

  // Rebuild filtered children for expanded groups; refresh issue count for collapsed groups so
  // the parent's issue icon reflects the *currently filtered* set, not the raw build-time count.
  private _rebuildAllFilteredChildren(): void {
    const ctx = this._filterContext()
    for (const group of this.expandableGroups.values()) {
      if (group.expanded) {
        this._rebuildFilteredChildren(group, ctx)
      } else {
        // Collapsed: skip the heavy filter/cap work but keep the issue-icon count honest.
        this._recomputeIssueCount(group, ctx)
      }
    }
  }

  // Snapshot the current global filter state once per pass so the inner predicate doesn't
  // re-read component fields per resource (saves ~5N property reads on big k8s groups).
  private _filterContext() {
    const hasTextFilter = !!this.filterText
    return {
      hasGlobalStatus: this.selectedStatusFilters.length > 0,
      hasGlobalType: this.selectedResourceTypes.length > 0,
      hasGlobalGroup: this.selectedResourceGroups.length > 0,
      hasTextFilter,
      textQuery: hasTextFilter ? this.filterText.toLowerCase() : '',
    }
  }

  // Apply only the global filter axes (status / type / group / text) — shared between the
  // expanded-group rebuild and the count-only path so the predicate stays in lockstep.
  // Pass `skipStatus: true` when the caller has already applied a per-group facet drill-in.
  private _passesGlobalFilters(
    r: ViewableApplicationResource,
    ctx: ReturnType<AppResourcesViewComponent['_filterContext']>,
    skipStatus = false
  ): boolean {
    if (!skipStatus && ctx.hasGlobalStatus && !this.selectedStatusFilters.includes(r.status)) return false
    if (ctx.hasGlobalType && !this.selectedResourceTypes.includes(r.displayResourceType)) return false
    if (ctx.hasGlobalGroup && !this.selectedResourceGroups.includes(r.sourceResourceGroup || '')) return false
    if (ctx.hasTextFilter) {
      const statusLabel = ((r as any)._statusLabel || '').toLowerCase()
      const matchesText =
        (r.displayResourceType || '').toLowerCase().includes(ctx.textQuery) ||
        (r.sourceResourceName || '').toLowerCase().includes(ctx.textQuery) ||
        (r.targetResourceName || '').toLowerCase().includes(ctx.textQuery) ||
        statusLabel.includes(ctx.textQuery)
      if (!matchesText) return false
    }
    return true
  }

  // Stamp the parent's _childIssueCount with the count of children that have issues AND pass
  // current global filters — i.e. what the user would actually see if they clicked the icon.
  // The per-group issuesOnly axis is intentionally ignored: clicking the icon toggles it, so
  // the badge should reflect the post-click set.
  private _recomputeIssueCount(group: ExpandableChildGroup, ctx = this._filterContext()): void {
    let count = 0
    for (const r of group.rawChildren) {
      if (!r.hasIssues) continue
      if (!this._passesGlobalFilters(r, ctx)) continue
      count++
    }
    group.issueCount = count
    if (group.parentRow) (group.parentRow as any)._childIssueCount = count
  }

  // Rebuild filtered children for a single expandable group.
  //
  // Global filters (status pills, type pills, group pills, global issues) narrow the children
  // so that when a parent is auto-expanded by a filter match, the user sees only the matching
  // descendants — not the full child list. Otherwise a user who filters "Failed" would see 5806
  // SUCCESS pods alongside the 39 FAILED ones they asked for.
  //
  // Per-group filters (per-group issues icon) narrow further — user intent *inside* a group.
  private _rebuildFilteredChildren(
    group: ExpandableChildGroup,
    ctx: ReturnType<AppResourcesViewComponent['_filterContext']> = this._filterContext()
  ): void {
    if (!group.children?.length) {
      group.filteredChildren = []
      this._recomputeIssueCount(group, ctx)
      return
    }

    // Per-group bypass: user explicitly chose "See children anyway", a hard reset on all narrowing.
    // Ignore every global axis AND the per-group issuesOnly so the user truly sees 100% of children
    // (otherwise a stale issuesOnly mask hides the very statuses that flipped the rollup pill —
    // FAILED / NOT_PROCESSED with hasIssues=false get filtered out, defeating the bypass).
    if (group.bypassGlobalFilters) {
      group.filteredChildren = group.children
      this._recomputeIssueCount(group, ctx)
      return
    }

    const hasGroupIssuesOnly = group.issuesOnly
    const hasGlobalIssues = this.issuesFilterActive
    const noFilters =
      !hasGroupIssuesOnly &&
      !ctx.hasGlobalStatus &&
      !ctx.hasGlobalType &&
      !ctx.hasGlobalGroup &&
      !hasGlobalIssues &&
      !ctx.hasTextFilter

    if (noFilters) {
      group.filteredChildren = group.children
      this._recomputeIssueCount(group, ctx)
      return
    }

    group.filteredChildren = group.children.filter((r) => {
      if (!this._passesGlobalFilters(r, ctx)) return false
      if (hasGroupIssuesOnly && !r.hasIssues) return false
      if (hasGlobalIssues && !r.hasIssues) return false
      return true
    })
    this._recomputeIssueCount(group, ctx)
  }

  /** Lazily sort and filter children on first expand to avoid upfront cost for large resource sets. */
  private _initGroupChildren(group: ExpandableChildGroup): void {
    if (group.initialized) return
    group.initialized = true

    group.rawChildren.sort((a, b) => {
      const typeCompare = a.displayResourceType.localeCompare(b.displayResourceType)
      if (typeCompare !== 0) return typeCompare
      return (a.sourceResourceName || '').localeCompare(b.sourceResourceName || '')
    })
    group.children = group.rawChildren

    // Rebuild per-resource caches to include these children (tooltips, placeholders)
    this.rebuildPerResourceCaches()
    this._rebuildFilteredChildren(group)
  }

  /** Toggle an expandable parent open/closed. On first expand, initializes children.
   *  Also flips the cell-text expand state so long values in this row's truncatable cells
   *  wrap on click — matches the click-to-expand behavior of regular `.resource-row` rows. */
  toggleExpandableParent(parentArn: string): void {
    const group = this.expandableGroups.get(parentArn)
    if (!group) return

    this.toggleCellExpand(parentArn)
    group.expanded = !group.expanded
    // Any manual toggle clears the auto-expand flag so later filter changes won't collapse
    // a group the user explicitly opened (or leave one they explicitly closed flagged as auto).
    group.autoExpanded = false
    this.cdr.markForCheck()
    if (group.expanded) {
      // Show spinner, defer heavy DOM render so the browser paints the spinner first
      group.loading = true
      setTimeout(() => {
        this._initGroupChildren(group)
        this._rebuildFilteredChildren(group)
        this.cdr.markForCheck() // Children populated — mark dirty so they render
        setTimeout(() => {
          group.loading = false
          this.cdr.markForCheck()
        })
      })
    } else {
      group.loading = true
      group.filteredChildren = []
      // Bypass is per-expand intent only — collapse drops it so the next expand starts clean.
      group.bypassGlobalFilters = false
      setTimeout(() => {
        group.loading = false
        this.cdr.markForCheck()
      })
    }
  }

  /** Per-group escape hatch from the empty-filter row: flip the bypass flag and rebuild this
   *  group's children. Bypass is cheap (no filter pass), so no deferred spinner needed. */
  toggleBypassGroupFilters(parentArn: string): void {
    const group = this.expandableGroups.get(parentArn)
    if (!group) return
    group.bypassGlobalFilters = !group.bypassGlobalFilters
    this._rebuildFilteredChildren(group)
    this.cdr.markForCheck()
  }

  /** Filter expandable children to show only resources with issues. Mirrors the Needs-attention
   *  pill's spinner-then-defer pattern so the user gets immediate visual feedback on big k8s
   *  groups, and so the heavy filter pass doesn't block the click. */
  filterToIssues(event: MouseEvent, parentArn: string): void {
    event.stopPropagation()
    const group = this.expandableGroups.get(parentArn)
    // Guard re-entry while the spinner is up so a double-click doesn't queue overlapping defers.
    if (!group || group.applyingIssues) return

    group.applyingIssues = true
    this.cdr.markForCheck()

    setTimeout(() => {
      const turningOn = !group.issuesOnly
      group.issuesOnly = turningOn
      group.expanded = true
      this._initGroupChildren(group)
      this._rebuildFilteredChildren(group)
      // Defensive: if turning the filter ON yields zero rows (e.g. the icon's count was stale
      // mid-poll), collapse so the caret stays in sync with the empty body. Without this the
      // body is empty but the caret reads "expanded".
      if (turningOn && group.filteredChildren.length === 0) {
        group.expanded = false
        group.issuesOnly = false
      }
      group.applyingIssues = false
      this.cdr.markForCheck()
    }, 180)
  }

  /** Clear the issues-only filter on an expandable group. */

  /**
   * Auto-expand expandable parents when a search query matches any of their children.
   * Auto-collapse when the query no longer matches (unless user manually expanded).
   */
  /**
   * Combined "does any child match the search?" pass over each expandable group. Replaces
   * the previous syncParentSearchFields + autoExpandParentsIfMatched pair, which walked
   * group.rawChildren twice with byte-identical predicates — wasted on big k8s groups.
   * Side effects per group:
   *   - parent row's `_childSearchMatch` is set/cleared so PrimeNG's global filter keeps
   *     parents visible when children match
   *   - group.expanded / autoExpanded toggles when the match flips
   * Order vs. _rebuildAllFilteredChildren matters: that function only iterates expanded
   * groups, so this must run first to flip expanded=true before children get rebuilt.
   */
  private syncAndAutoExpand(filterValue: string): void {
    // Empty-search clear path: collapse anything we auto-expanded; leave _childSearchMatch alone
    // (PrimeNG's global filter is already cleared in applyFilter, so stale stamps are harmless).
    if (!filterValue) {
      for (const group of this.expandableGroups.values()) {
        if (group.autoExpanded) {
          group.filteredChildren = []
          group.expanded = false
          group.autoExpanded = false
        }
      }
      return
    }

    // Build an arn→row lookup so we can update the parent row's _childSearchMatch in one pass
    // over expandableGroups (instead of a second pass over displayRows).
    const rowByArn = new Map<string, ViewableApplicationResource>()
    if (this.displayRows) {
      for (const row of this.displayRows) {
        if ((row as any)._hasExpandableChildren) rowByArn.set(row.resourceArn, row)
      }
    }

    const query = filterValue.toLowerCase()
    for (const [arn, group] of this.expandableGroups) {
      const hasMatch = group.rawChildren.some((r) => {
        const statusLabel = ((r as any)._statusLabel || '').toLowerCase()
        return (
          (r.displayResourceType || '').toLowerCase().includes(query) ||
          (r.sourceResourceName || '').toLowerCase().includes(query) ||
          (r.targetResourceName || '').toLowerCase().includes(query) ||
          statusLabel.includes(query)
        )
      })

      const row = rowByArn.get(arn)
      if (row) {
        if (hasMatch) (row as any)._childSearchMatch = filterValue
        else delete (row as any)._childSearchMatch
      }

      if (hasMatch && !group.expanded) {
        group.expanded = true
        this._initGroupChildren(group)
        group.autoExpanded = true
      } else if (!hasMatch && group.autoExpanded) {
        group.filteredChildren = []
        group.expanded = false
        group.autoExpanded = false
      }
    }
  }

  private updateGlobalFilterFields() {
    const baseFields = ['status', 'displayResourceType']
    const resourceGroupFields = []

    if (this._shouldShowResourceGroupColumn && !this.appIsDeleted) {
      resourceGroupFields.push('sourceResourceGroup')
    }

    const resourceNameFields = this.appIsDeleted ? ['targetResourceName'] : ['sourceResourceName', 'targetResourceName']

    // _childSearchMatch is injected on expandable parent rows by syncParentSearchFields()
    // so PrimeNG's global filter keeps them visible when their children match the search query
    // _statusLabel is stamped by _stampStatusLabels() so global filter matches status text
    this._globalFilterFields = [
      ...baseFields,
      ...resourceGroupFields,
      ...resourceNameFields,
      '_childSearchMatch',
      '_statusLabel',
    ]
  }

  private updateNotEmptyList() {
    if (!this.resources) {
      this._notEmptyList = false
      return
    }
    this._notEmptyList =
      (this.tabType !== ResourcesTab.FAILBACK && this.resources.length > 0) ||
      (this.tabType === ResourcesTab.FAILBACK && this.resources.some((val) => val.status !== ResourceStatus.NO_STATUS))
  }
}
